import React, { useState, useCallback, useRef, useEffect } from 'react';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { ref, get, update } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import { firestore, database, functions } from '../firebaseConfig';
import { CLOUD_FUNCTION_URLS, authenticatedFetch } from '../utils/cloudFunctions';
import { computeAndStoreSignals } from './leadSignalEngine';
import { costOf } from '../config/creditPlans';
import useCreditBalance from '../hooks/useCreditBalance';
import { lookupBlacklistForEmails, isEmailBlacklisted } from '../shared/blacklistUtils';
import { logContactActivity, logStatusChange } from '../shared/contactActivityLog';
import ResearchLockedNotice from './ResearchLockedNotice';
import { RESEARCH_ALL_LOCKED } from '../config/researchFeatures';
import { bulkResearchDoneLog } from './bulkResearchToast';
import {
  IG_RESEARCH_SETTINGS_PATH,
  languageFromIgSettings,
  buildIgResearchDescriptionBlock,
} from './instagramResearchLanguage';
import {
  potentialProfilesPath,
  potentialLegacyPath,
  profilesFromSnapshot,
  resolvePotentialProfile,
  buildPotentialIcp,
} from './potentialProfiles';
import { recordCompanyRecord } from '../shared/recordIntelligenceLog';
import {
  REVIEW_AUDIT_FAILURES,
  REVIEW_AUDIT_CONCURRENCY,
  MAX_THROTTLE_STRIKES,
  MAX_BUSY_RETRIES,
  reviewAuditGapMs,
  reviewAuditBackoffMs,
  isReviewAuditEligible,
  requestGoogleReviewAudit,
  buildReviewAuditUpdate,
} from './googleReviewAudit';
import { useDataConnectors, runDataConnector, connectorGapMs, reasonLabel as connectorReasonLabel } from './dataConnectors';
import './BulkResearchModal.css';

const IG_RESEARCH_CREDIT_COST = costOf('instagramResearch');
const REVIEW_AUDIT_CREDIT_COST = costOf('googleReviewAudit');
const DM_FINDER_CREDIT_COST = costOf('dmEmailFinder');

function normalizeDomain(raw) {
  if (!raw || typeof raw !== 'string') return '';
  let d = raw.trim().toLowerCase();
  d = d.replace(/^https?:\/\//, '');
  d = d.replace(/^www\./, '');
  d = d.split('/')[0];
  d = d.split('?')[0];
  return d.includes('.') ? d : '';
}

const SEO_RATE_LIMIT_MS = 400;

function extractHandle(raw) {
  if (!raw) return '';
  return raw
    .replace(/^https?:\/\/(www\.)?instagram\.com\//, '')
    .replace(/^@/, '')
    .replace(/[?#].*$/, '')
    .replace(/\/+$/, '')
    .trim();
}

const IG_RATE_LIMIT_MS = 550;

function parseDMs(raw) {
  if (!raw) return [];
  try { return typeof raw === 'string' ? JSON.parse(raw) : Array.isArray(raw) ? raw : []; }
  catch (_) { return []; }
}

const POTENTIAL_CREDIT_COST = costOf('potentialAnalyzer');
// Per contact. The hyper cleaner runs a larger model, hence its own price.
const CLEAN_CREDIT_COST = costOf('contactClean');
const CLEAN_HYPER_CREDIT_COST = costOf('contactCleanHyper');

// ── Smart Funnel combo helpers ──
// The funnel chains Clean → Potential Analyzer → Web Research → Find Emails.
// The Clean and Potential stages are run here directly against the same backend
// cloud functions used by the standalone Cleaner (AiCleanerPopup) and Potential
// Analyzer (usePotentialAnalyzer). The Web Research → Find Emails legs are handed
// off to the EXISTING combo path (onStartCombo) so none of that logic changes.

// Keep only contacts whose potential score is strictly above this value.
const FUNNEL_MIN_SCORE = 5;

// The Smart Funnel runs four heavy stages per contact — cap the batch size.
const FUNNEL_MAX_CONTACTS = 50;

// Safety net: if the chosen cleaner flags at least this share of a meaningfully
// sized batch for removal, that almost always means the wrong/too-strict cleaner
// was picked (or the data is too sparse pre-research). We then delete NOTHING and
// stop the funnel with a warning instead of nuking the list.
const FUNNEL_REMOVE_SAFETY_RATIO = 0.8;
const FUNNEL_REMOVE_SAFETY_MIN = 5;

// Mirrors usePotentialAnalyzer: a re-run replaces the old note instead of stacking.
const POTENTIAL_NOTE_RE = /\s*\[Potential\s+\d+\/10\][\s\S]*$/i;

function buildPotentialNote(currentDescription, score, summary) {
  if (!summary) return null;
  const base = String(currentDescription || '').replace(POTENTIAL_NOTE_RE, '').trimEnd();
  const note = `[Potential ${score}/10] ${summary}`;
  return base ? `${base}\n\n${note}` : note;
}

// Ported from AiCleanerPopup — normalizes a guideline section into a list, or
// returns free text unchanged.
function normalizeGuidelineSection(raw) {
  if (!raw || !raw.trim()) return '';
  const text = raw.trim();
  const hasListMarkers = /^[-•*]\s/m.test(text);
  const isStructuredInput = hasListMarkers || text.includes(',') || text.includes(';') || text.includes('\n');
  if (!isStructuredInput) return text;
  const items = text
    .split(/[\n;]+/)
    .flatMap(line => {
      const trimmed = line.trim();
      if (!trimmed) return [];
      const cleaned = trimmed.replace(/^[-•*]\s*/, '');
      if (cleaned.includes(',')) return cleaned.split(',').map(s => s.trim()).filter(Boolean);
      return [cleaned];
    })
    .filter(Boolean);
  return items.map(item => `- ${item}`).join('\n');
}

// Ported from AiCleanerPopup — merges keep/remove sections into a guidelines string.
function combineGuidelineSections(guideline) {
  if (!guideline) return '';
  if (guideline.keepSection || guideline.removeSection) {
    let combined = '';
    if (guideline.keepSection) combined += `KEEP section - Businesses to keep:\n\n${normalizeGuidelineSection(guideline.keepSection)}\n\n`;
    if (guideline.removeSection) combined += `REMOVE section - Businesses to exclude:\n\n${normalizeGuidelineSection(guideline.removeSection)}\n\n`;
    return combined.trim();
  }
  return guideline.content || '';
}

// Ported from usePotentialAnalyzer.buildPayload — scores against one ICP profile.
function buildPotentialPayload(contact, profile) {
  return {
    businessName: contact.business_name || '',
    websiteUrl: contact.lead_website || '',
    description: contact.lead_description || '',
    email: contact.lead_email || '',
    city: contact.lead_city || '',
    country: contact.lead_country || '',
    icp: buildPotentialIcp(profile),
    language: profile?.language || 'auto',
    mode: 'combo',
  };
}

// Ported from AiCleanerPopup.analyzeOneContact payload shape.
function buildCleanPayload(contact, guidelines, isHyper, businessContext) {
  const payload = {
    businessName: contact.business_name || '',
    description: contact.lead_description || '',
    email: contact.lead_email || '',
    guidelines,
    hyperIntelligence: isHyper,
    website: contact.lead_website || '',
    instagramCategory: contact.lead_instagram_category || '',
    city: contact.lead_city || '',
    country: contact.lead_country || '',
    businessType: contact.lead_type || '',
    instagram: contact.lead_instagram || '',
    facebook: contact.lead_facebook || '',
    linkedin: contact.lead_linkedin || '',
  };
  if (businessContext) payload.businessContext = businessContext;
  if (contact.lead_score) payload.leadScore = contact.lead_score;
  if (contact.spy_confidence_score) payload.spyConfidence = contact.spy_confidence_score;
  if (contact.lead_decision_makers) {
    try {
      const dms = typeof contact.lead_decision_makers === 'string' ? JSON.parse(contact.lead_decision_makers) : contact.lead_decision_makers;
      if (Array.isArray(dms) && dms.length > 0) {
        payload.decisionMakers = dms.map(dm => [dm.name, dm.role].filter(Boolean).join(' — ')).join(', ');
      }
    } catch (e) { /* ignore parse errors */ }
  }
  return payload;
}

// Loads the full list of cleanup guidelines (newest first) so the funnel can
// let the user pick WHICH cleaner to run instead of silently grabbing the first
// active one. Returns [] when none are configured.
async function loadCleanupGuidelines(userId) {
  try {
    const snap = await get(ref(database, `/World/${userId}/settings/cleanupGuidelinesList`));
    if (!snap.exists()) return [];
    const list = snap.val() || {};
    return Object.keys(list)
      .map(key => ({ id: key, ...list[key] }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
  } catch (_) {
    return [];
  }
}

// Counts the KEEP/REMOVE rules in a guideline so the picker can show how
// substantial each cleaner is (a guideline with no KEEP rules will reject
// almost everything — the user should see that before choosing it).
function countGuidelineRules(guideline) {
  const countLines = (txt) => (txt ? String(txt).split('\n').filter(l => l.trim().startsWith('-')).length : 0);
  const keep = countLines(guideline.keepSection);
  const remove = countLines(guideline.removeSection);
  if (keep === 0 && remove === 0 && guideline.content) {
    return { keep: 0, remove: 0, legacy: countLines(guideline.content) };
  }
  return { keep, remove, legacy: 0 };
}

async function loadPotentialProfiles(userId) {
  try {
    const snap = await get(ref(database, potentialProfilesPath(userId)));
    return profilesFromSnapshot(snap.exists() ? snap.val() : null);
  } catch (_) {
    return [];
  }
}

// Resolves which ICP profile a run scores against — same order as
// resolvePotentialProfile / loadPotentialProfile in the outreach job.
async function loadPotentialProfileForRun(userId, profileId) {
  try {
    const [listSnap, legacySnap] = await Promise.all([
      get(ref(database, potentialProfilesPath(userId))),
      get(ref(database, potentialLegacyPath(userId))),
    ]);
    const profiles = profilesFromSnapshot(listSnap.exists() ? listSnap.val() : null);
    const legacy = legacySnap.exists() ? (legacySnap.val() || {}) : {};
    return resolvePotentialProfile(profiles, legacy, profileId);
  } catch (_) {
    return resolvePotentialProfile([], {}, profileId);
  }
}

/**
 * Karte für ein Tool ohne Lizenz. Bleibt sichtbar statt zu verschwinden, damit
 * der Kunde sieht, was es gibt — und es direkt anfragen kann.
 */
const BrmLockedTool = ({ name, icon, iconClass, context }) => (
  <div className="brm-tool brm-tool--locked">
    {/* Statt der Checkbox ein Schloss: Eine Checkbox würde Auswählbarkeit
        suggerieren, hält aber die Spaltenflucht zu den offenen Karten. */}
    <div className="brm-tool-lockslot"><i className="fa fa-lock" /></div>
    <div className={`brm-tool-icon ${iconClass || ''}`}><i className={icon} /></div>
    <div className="brm-tool-content">
      <div className="brm-tool-top">
        <span className="brm-tool-name">{name}</span>
        <span className="brm-tool-count brm-tool-count--locked">Locked</span>
      </div>
      <ResearchLockedNotice
        featureName={name}
        accountEmail={context?.accountEmail}
        worldId={context?.worldId}
      />
    </div>
  </div>
);

const BulkResearchModal = ({ isOpen, onClose, contacts, userId, research = RESEARCH_ALL_LOCKED, researchLoading = false, researchContext = {}, seoKeyReady, potentialEnabled, hasDecisionMakerResearch, onStartWebSpy, onStartWebsiteAnalyzer, onStartPotential, onStartCombo, autoStartDmFinder, autoFinish, funnelStats, analyzerCreditCost, onContactUpdate }) => {
  const { balance: creditBalance } = useCreditBalance(userId);
  const [selected, setSelected] = useState({ web: false, instagram: false, reviews: false, dmFinder: false, websiteAnalyzer: false, seo: false, potential: false, combo: false, funnel: false });
  // Custom researchers are dynamic (one per data connector), so they're picked by id.
  const [connectorPick, setConnectorPick] = useState([]);
  const [runningConnector, setRunningConnector] = useState('');
  const { connectors: dataConnectors } = useDataConnectors(isOpen ? userId : null);
  const [running, setRunning] = useState(null);
  // Tracks which leg of the Smart Funnel is currently running ('clean' | 'potential')
  // so the progress toast can show a meaningful label.
  const [funnelStage, setFunnelStage] = useState(null);
  // Smart Funnel step 1: pick which cleaner (guideline) to run before starting.
  const [funnelPicker, setFunnelPicker] = useState(false);
  const [funnelGuidelines, setFunnelGuidelines] = useState([]);
  const [funnelGuidelineId, setFunnelGuidelineId] = useState(null);
  const [funnelLoadingGuidelines, setFunnelLoadingGuidelines] = useState(false);
  // After the cleaner (or for standalone Potential), pick which ICP profile
  // to score against — same pattern as the cleaner guideline picker.
  const [potentialPicker, setPotentialPicker] = useState(false);
  const [potentialPickerProfiles, setPotentialPickerProfiles] = useState([]);
  const [potentialPickerId, setPotentialPickerId] = useState(null);
  const [potentialPickerMode, setPotentialPickerMode] = useState(null); // 'funnel' | 'standalone'
  const [pendingFunnelGuideline, setPendingFunnelGuideline] = useState(undefined);
  const [profilePickerLoading, setProfilePickerLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [total, setTotal] = useState(0);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(false);
  const [webStarted, setWebStarted] = useState(false);
  const [liveLog, setLiveLog] = useState([]);
  const [showSummary, setShowSummary] = useState(false);
  const [dmStats, setDmStats] = useState({ standard: 0, expanded: 0, osint: 0, catchAll: 0, notFound: 0, total: 0 });
  const [toastFading, setToastFading] = useState(false);
  // Contacts whose discovered DM email matched the blacklist. Surfaced in
  // the summary modal so the user can remove them in one click.
  const [blacklistHits, setBlacklistHits] = useState([]); // [{ id, name, emails: [{name, email}] }]
  const [removingBlacklist, setRemovingBlacklist] = useState(false);
  // Ultimate Research: when the funnel reaches its final DM Email Finder leg we
  // auto-confirm (there's nothing left to review) and show this completion modal.
  const [ultimateDone, setUltimateDone] = useState(false);
  const [ultimateStats, setUltimateStats] = useState({ started: 0, removed: 0, qualified: 0, emails: 0, enriched: 0 });
  const autoFinishedRef = useRef(false);
  const startTimeRef = useRef(null);
  const cancelRef = useRef(false);
  const logIdRef = useRef(0);

  useEffect(() => {
    // Don't auto-dismiss the toast while the Ultimate Research completion modal
    // is up — the user closes that one themselves.
    if (!done || showSummary || ultimateDone) return;
    setToastFading(false);
    const fadeTimer = setTimeout(() => setToastFading(true), 3000);
    const dismissTimer = setTimeout(() => resetState(), 4000);
    return () => { clearTimeout(fadeTimer); clearTimeout(dismissTimer); };
  }, [done, showSummary, ultimateDone]);

  const addLog = useCallback((icon, text) => {
    const id = ++logIdRef.current;
    setLiveLog(prev => [...prev.slice(-8), { id, icon, text }]);
  }, []);

  // Combo step 2: once Web Research has finished and its freshly-discovered
  // decision makers have been applied, the parent re-opens this modal with
  // `autoStartDmFinder` set. We kick off the DM Email Finder automatically on
  // the now-enriched contacts — no extra click for the user. The ref holds the
  // exact contacts batch we already auto-ran for, so a fresh batch (new array
  // reference) re-triggers while the same one never double-fires.
  const autoComboRef = useRef(null);

  const webContacts = contacts.filter(c => (c.business_name?.trim()) || (c.lead_website?.trim()));
  const igContacts = contacts.filter(c => extractHandle(c.lead_instagram));
  const reviewContacts = contacts.filter(c => isReviewAuditEligible(c) && !c.researchIntelligence?.googleReviewAudit);
  const dmFinderContacts = contacts.filter(c => {
    const dms = parseDMs(c.lead_decision_makers);
    return dms.some(dm => dm.name && dm.name.trim().length >= 3 && (!dm.email || !dm.email.includes('@')));
  });

  const waContacts = contacts.filter(c => c.lead_website?.trim() && !c.websiteAnalysis);

  // SEO: contacts with a valid website and no existing SEO scan yet
  const seoContacts = contacts.filter(c => normalizeDomain(c.lead_website) && !c.researchIntelligence?.seoAnalysis?.data);

  // Potential Analyser: any contact with a business name or website is eligible
  const potentialContacts = contacts.filter(c => (c.business_name?.trim()) || (c.lead_website?.trim()));

  // Solange die Lizenzen laden, gilt nichts als gesperrt — sonst blitzen beim
  // Öffnen kurz lauter Schlösser auf.
  const licensed = (key) => researchLoading || research[key] === true;

  const lockedWeb = !licensed('hasWebResearch');
  const lockedWa = !licensed('hasWebsiteAnalyzer');
  const lockedIg = !licensed('hasInstagramResearch');
  const lockedReviews = !licensed('hasGoogleReviewAudit');
  const lockedSeo = !licensed('hasSeoAnalyzer');
  const lockedPotential = !licensed('hasPotentialAnalyzer');
  const lockedDmFinder = !licensed('hasDmEmailFinder');
  const lockedConnectors = !licensed('hasDataConnectors');
  // Contacts without data from this connector yet; the backend skips contacts
  // that lack a field the platform needs.
  const connectorContactsFor = (connector) => contacts.filter((c) => !c.researchIntelligence?.connectors?.[connector.id]);
  const pickedConnectors = lockedConnectors
    ? []
    : dataConnectors.filter((c) => connectorPick.includes(c.id) && connectorContactsFor(c).length > 0);

  const canSelectWeb = !lockedWeb && webContacts.length > 0;
  const canSelectIg = !lockedIg && igContacts.length > 0;
  const canSelectReviews = !lockedReviews && reviewContacts.length > 0;
  const canSelectDmFinder = !lockedDmFinder && dmFinderContacts.length > 0;
  const canSelectWa = !lockedWa && waContacts.length > 0;
  const canSelectSeo = !lockedSeo && seoKeyReady && seoContacts.length > 0;
  const canSelectPotential = !lockedPotential && potentialEnabled && potentialContacts.length > 0;
  // Combo is offered whenever there's something to research AND Web Research's
  // Decision-Makers module is active (so research will surface DMs the second
  // leg can chase). It does NOT require contacts to already have DMs.
  // Beide Legs brauchen ihre eigene Lizenz — deshalb hängt Combo zusätzlich am
  // DM Email Finder.
  const canCombo = canSelectWeb && hasDecisionMakerResearch && !lockedDmFinder;
  // Smart Funnel needs the same prerequisites as the Web→Find-Emails combo
  // (research with decision makers) PLUS the Potential Analyzer being enabled,
  // since the funnel filters on the potential score before researching.
  const funnelPrereqsMet = canCombo && !lockedPotential && potentialEnabled;
  // The funnel chains four heavy stages per contact, so we cap it to keep runs
  // sane. Over the limit the card is shown but greyed out (not hidden).
  const funnelOverLimit = contacts.length > FUNNEL_MAX_CONTACTS;
  const canFunnel = funnelPrereqsMet && !funnelOverLimit;
  // Während die Lizenzen noch laden gilt oben nichts als gesperrt (sonst
  // flackern die Karten). Der Start bleibt so lange trotzdem blockiert.
  const hasSelection = !researchLoading && ((selected.funnel && canFunnel) || (selected.combo && canCombo) || (selected.web && canSelectWeb) || (selected.instagram && canSelectIg) || (selected.reviews && canSelectReviews) || (selected.dmFinder && canSelectDmFinder) || (selected.websiteAnalyzer && canSelectWa) || (selected.seo && canSelectSeo) || (selected.potential && canSelectPotential) || pickedConnectors.length > 0);

  const resetState = () => {
    cancelRef.current = true;
    setRunning(null);
    setFunnelStage(null);
    setFunnelPicker(false);
    setFunnelGuidelines([]);
    setFunnelGuidelineId(null);
    setFunnelLoadingGuidelines(false);
    setPotentialPicker(false);
    setPotentialPickerProfiles([]);
    setPotentialPickerId(null);
    setPotentialPickerMode(null);
    setPendingFunnelGuideline(undefined);
    setProfilePickerLoading(false);
    setProgress(0);
    setTotal(0);
    setResults([]);
    setDone(false);
    setWebStarted(false);
    setLiveLog([]);
    setShowSummary(false);
    setToastFading(false);
    setDmStats({ standard: 0, expanded: 0, osint: 0, catchAll: 0, notFound: 0, total: 0 });
    setSelected({ web: false, instagram: false, reviews: false, dmFinder: false, websiteAnalyzer: false, seo: false, potential: false, combo: false, funnel: false });
    setConnectorPick([]);
    setRunningConnector('');
    setBlacklistHits([]);
    setRemovingBlacklist(false);
    setUltimateDone(false);
    setUltimateStats({ started: 0, removed: 0, qualified: 0, emails: 0, enriched: 0 });
    autoFinishedRef.current = false;
    startTimeRef.current = null;
  };

  const handleClose = () => {
    if (running && !done) {
      onClose();
      return;
    }
    resetState();
    onClose();
  };

  const handleDismissToast = () => {
    cancelRef.current = true;
    resetState();
  };

  const toggleConnector = (id) => {
    setConnectorPick((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
    setSelected((prev) => ({ ...prev, combo: false, funnel: false }));
  };

  const toggleSelect = (key) => {
    if (key === 'combo' || key === 'funnel') setConnectorPick([]);
    setSelected(prev => {
      // The combos are their own modes (chained pipelines), so picking one clears
      // the individual tools AND the other combo, and picking any individual tool
      // clears both combos. Keeps a single, unambiguous intent per run.
      if (key === 'combo') {
        return prev.combo
          ? { ...prev, combo: false }
          : { web: false, instagram: false, reviews: false, dmFinder: false, websiteAnalyzer: false, seo: false, potential: false, combo: true, funnel: false };
      }
      if (key === 'funnel') {
        return prev.funnel
          ? { ...prev, funnel: false }
          : { web: false, instagram: false, reviews: false, dmFinder: false, websiteAnalyzer: false, seo: false, potential: false, combo: false, funnel: true };
      }
      return { ...prev, combo: false, funnel: false, [key]: !prev[key] };
    });
  };

  const deductCredits = useCallback(async (contactId, handle) => {
    const deductCreditsFn = httpsCallable(functions, 'deductCredits');
    await deductCreditsFn({ action: 'instagram_research', amount: IG_RESEARCH_CREDIT_COST, worldId: userId, metadata: { contactId, handle } });
  }, [userId]);

  const startIgBatch = useCallback(async () => {
    if (!userId || igContacts.length === 0) return;
    cancelRef.current = false;
    setRunning('instagram');
    setTotal(igContacts.length);
    setProgress(0);
    setResults([]);
    setDone(false);
    startTimeRef.current = Date.now();

    let insightLanguage = languageFromIgSettings(null);
    try {
      const langSnap = await get(ref(database, IG_RESEARCH_SETTINGS_PATH(userId)));
      insightLanguage = languageFromIgSettings(langSnap.val());
    } catch (_) { /* keep default */ }

    const batchResults = [];

    for (let i = 0; i < igContacts.length; i++) {
      if (cancelRef.current) break;

      const contact = igContacts[i];
      const handle = extractHandle(contact.lead_instagram);
      const result = { id: contact.id, handle, name: contact.business_name || handle };

      try {
        await deductCredits(contact.id, handle);
      } catch (err) {
        const isCreditErr = (err.message || '').includes('Not enough credits') || err?.code === 'resource-exhausted';
        result.status = 'error';
        result.error = isCreditErr
          ? `Not enough credits — stopped after ${i} of ${igContacts.length} contacts.`
          : (err.message || 'Unknown error');
        batchResults.push(result);
        setResults([...batchResults]);
        setProgress(i + 1);
        break;
      }

      try {
        const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.instagramResearch(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ handle, language: insightLanguage }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.message || `HTTP ${res.status}`);
        }

        const json = await res.json();
        result.status = 'success';
        result.score = json.metrics?.activityScore;
        result.posts = json.posts?.length || 0;

        const ai = json.aiInsight;
        const m = json.metrics || {};
        const igBlock = buildIgResearchDescriptionBlock({
          ai,
          metrics: m,
          followerCount: json.user?.follower_count,
          language: insightLanguage,
        });

        const existingDesc = contact.lead_description || '';
        const descClean = existingDesc.replace(/\n?\[IG Research\].*$/s, '').trim();
        const newDesc = igBlock
          ? (descClean ? `${descClean}\n[IG Research] ${igBlock}` : `[IG Research] ${igBlock}`)
          : descClean;

        const instagramInsight = ai || null;
        const instagramPosts = (json.posts || []).map(p => ({
          text: (p.text || '').substring(0, 500),
          like_count: p.like_count || 0,
          comment_count: p.comment_count || 0,
          created_at: p.created_at || null,
          media_format: p.media_format || 'image',
          thumbnail_url: p.thumbnail_url || null,
        }));
        const instagramMeta = {
          ...m,
          handle,
          fullname: json.user?.fullname || null,
          fetchedAt: new Date().toISOString(),
        };

        const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
        const savePayload = {
          'researchIntelligence.instagramInsight': instagramInsight,
          'researchIntelligence.instagramPosts': instagramPosts,
          'researchIntelligence.instagramMeta': instagramMeta,
          lead_description: newDesc,
          lead_instagram: handle ? `https://instagram.com/${handle}` : '',
          ...(json.user?.follower_count != null ? { lead_instagram_followers: json.user.follower_count } : {}),
          ...(ai?.topics?.[0] ? { lead_instagram_category: ai.topics[0] } : {}),
        };
        await updateDoc(contactRef, savePayload);
        await recordCompanyRecord(userId, contact.id, 'instagram', contact, {
          ...contact,
          researchIntelligence: {
            ...(contact.researchIntelligence || {}),
            instagramInsight,
            instagramPosts,
            instagramMeta,
          },
          lead_instagram_followers: json.user?.follower_count ?? contact.lead_instagram_followers,
        });

        try {
          const freshData = { ...contact, ...savePayload, lead_description: newDesc };
          computeAndStoreSignals(userId, contact.id, freshData, { force: true }).catch(() => {});
        } catch (_) {}

        if (onContactUpdate) {
          // Include the researchIntelligence data so the row's IG badge/button/tab
          // (which gate on researchIntelligence.instagramInsight) show instantly
          // — without it the data only appeared after a hard refresh. The parent
          // deep-merges researchIntelligence against the live row.
          onContactUpdate(contact.id, {
            researchIntelligence: { instagramInsight, instagramPosts, instagramMeta },
            lead_description: newDesc,
            lead_instagram: handle ? `https://instagram.com/${handle}` : '',
            ...(json.user?.follower_count != null ? { lead_instagram_followers: json.user.follower_count } : {}),
            ...(ai?.topics?.[0] ? { lead_instagram_category: ai.topics[0] } : {}),
          });
        }
      } catch (err) {
        result.status = 'error';
        result.error = err.message;

        // If profile not found, clear the invalid Instagram handle
        const msg = (err.message || '').toLowerCase();
        const isNotFound = msg.includes('404') || msg.includes('not found') || msg.includes('user not found');
        if (isNotFound) {
          result.notFound = true;
          try {
            const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
            await updateDoc(contactRef, { lead_instagram: '', has_instagram: false });
            if (onContactUpdate) onContactUpdate(contact.id, { lead_instagram: '', has_instagram: false });
          } catch (_) {}
        }
      }

      batchResults.push(result);
      setResults([...batchResults]);
      setProgress(i + 1);

      if (i < igContacts.length - 1 && !cancelRef.current) {
        await new Promise(r => setTimeout(r, IG_RATE_LIMIT_MS));
      }
    }

    setDone(true);
  }, [userId, igContacts, deductCredits]);

  // ── Google Review Audit batch (small worker pool, charged per delivered audit) ──
  const startReviewBatch = useCallback(async () => {
    if (!userId || reviewContacts.length === 0) return;
    cancelRef.current = false;
    setRunning('reviews');
    setTotal(reviewContacts.length);
    setProgress(0);
    setResults([]);
    setDone(false);
    startTimeRef.current = Date.now();

    const deductCreditsFn = httpsCallable(functions, 'deductCredits');
    const queue = [...reviewContacts];
    const batchResults = [];
    let stopped = false;
    let pauseUntil = 0;
    let lastThrottleAt = 0;
    let strikes = 0;
    let busyRetries = 0;

    const wait = async (ms) => {
      const end = Date.now() + ms;
      while (Date.now() < end && !cancelRef.current) {
        await new Promise((resolve) => setTimeout(resolve, Math.min(500, end - Date.now())));
      }
    };

    const auditOne = async (contact) => {
      const name = contact.business_name || contact.lead_website || 'Contact';
      const result = { id: contact.id, name };
      let json;
      try {
        json = await requestGoogleReviewAudit({ userId, contact });
      } catch (err) {
        return { ...result, status: 'error', error: err.message };
      }
      if (!json.ok && (json.reason === 'rate_limited' || json.reason === 'busy')) {
        return { ...result, throttled: true, reason: json.reason, retryAfterSec: json.retryAfterSec };
      }
      if (!json.ok) {
        addLog('fa-circle-minus', `${name}: ${REVIEW_AUDIT_FAILURES[json.reason] || json.reason}`);
        return { ...result, status: 'skipped', error: REVIEW_AUDIT_FAILURES[json.reason] || json.reason };
      }
      try {
        await deductCreditsFn({
          action: 'google_review_audit',
          amount: REVIEW_AUDIT_CREDIT_COST,
          worldId: userId,
          metadata: { contactId: contact.id, featureId: json.place?.featureId || null },
        });
      } catch (err) {
        stopped = true;
        const isCreditErr = (err.message || '').includes('Not enough credits') || err?.code === 'functions/resource-exhausted' || err?.code === 'resource-exhausted';
        return { ...result, status: 'error', error: isCreditErr ? 'Not enough credits — run stopped.' : (err.message || 'Credit deduction failed') };
      }
      try {
        const { audit, firestore: payload, rowPatch } = buildReviewAuditUpdate(json);
        await updateDoc(doc(firestore, `World/${userId}/db/${contact.id}`), payload);
        await recordCompanyRecord(userId, contact.id, 'google_reviews', contact, {
          ...contact,
          researchIntelligence: { ...(contact.researchIntelligence || {}), googleReviewAudit: audit },
        });
        if (onContactUpdate) {
          onContactUpdate(contact.id, { researchIntelligence: { googleReviewAudit: audit }, ...rowPatch });
        }
        const topComplaint = audit.analysis?.complaints?.[0]?.theme;
        addLog('fa-star', `${name}: ${audit.place?.rating ?? '–'}★${topComplaint ? ` · ${topComplaint}` : ''}`);
        return { ...result, status: 'success', rating: audit.place?.rating ?? null };
      } catch (err) {
        return { ...result, status: 'error', error: err.message };
      }
    };

    const handleThrottle = (contact, result, startedAt) => {
      queue.unshift(contact);
      // Both workers can hit the same block; only the first one counts as a strike.
      const fresh = lastThrottleAt < startedAt;
      lastThrottleAt = Date.now();
      if (result.reason === 'busy') {
        busyRetries += 1;
        if (busyRetries > MAX_BUSY_RETRIES) stopped = true;
      } else if (fresh) {
        strikes += 1;
        if (strikes > MAX_THROTTLE_STRIKES) stopped = true;
      }
      if (stopped) {
        addLog('fa-hand', `Stopped to protect the account — ${queue.length} contact${queue.length === 1 ? '' : 's'} left, run them again later`);
        return;
      }
      const ms = reviewAuditBackoffMs({ reason: result.reason, retryAfterSec: result.retryAfterSec, strike: strikes });
      if (Date.now() + ms > pauseUntil) {
        pauseUntil = Date.now() + ms;
        addLog('fa-hourglass-half', result.reason === 'busy'
          ? `Review Audit busy — retrying in ${Math.round(ms / 1000)}s`
          : `Google needs a break — pausing ${Math.round(ms / 60000) || 1} min, then continuing`);
      }
    };

    const worker = async (index) => {
      // Stagger the second worker so the two never start in lockstep.
      let first = index === 0;
      while (queue.length > 0 && !cancelRef.current && !stopped) {
        if (!first) await wait(reviewAuditGapMs());
        first = false;
        if (pauseUntil > Date.now()) await wait(pauseUntil - Date.now());
        if (cancelRef.current || stopped) break;
        const contact = queue.shift();
        if (!contact) break;
        const startedAt = Date.now();
        const result = await auditOne(contact);
        if (result.throttled) {
          handleThrottle(contact, result, startedAt);
          continue;
        }
        if (result.status === 'success') {
          strikes = 0;
          busyRetries = 0;
        }
        batchResults.push(result);
        setResults([...batchResults]);
        setProgress(batchResults.length);
      }
    };

    await Promise.all(
      Array.from({ length: Math.min(REVIEW_AUDIT_CONCURRENCY, queue.length) }, (_, i) => worker(i))
    );
    setDone(true);
  }, [userId, reviewContacts, onContactUpdate, addLog]);

  // ── Custom researcher batch (one connector, sequential, spaced) ──
  // A broken setup (bad key, changed API) stops the run at once — every other
  // contact would fail the same way, and the backend has already raised the
  // one-shot alert. Platform rate limits pause and retry, up to 3 times.
  const startConnectorBatch = useCallback(async (connector) => {
    const targets = contacts.filter((c) => !c.researchIntelligence?.connectors?.[connector.id]);
    if (!userId || targets.length === 0) return;
    cancelRef.current = false;
    setRunning('connector');
    setRunningConnector(connector.name || 'Custom Researcher');
    setTotal(targets.length);
    setProgress(0);
    setResults([]);
    setDone(false);
    startTimeRef.current = Date.now();

    const wait = async (ms) => {
      const end = Date.now() + ms;
      while (Date.now() < end && !cancelRef.current) {
        await new Promise((resolve) => setTimeout(resolve, Math.min(500, end - Date.now())));
      }
    };

    const queue = [...targets];
    const batchResults = [];
    let throttles = 0;
    let first = true;
    while (queue.length > 0 && !cancelRef.current) {
      if (!first) await wait(Math.max(connectorGapMs(), (Number(connector.paceSec) || 0) * 1000));
      first = false;
      const contact = queue.shift();
      const name = contact.business_name || contact.lead_website || 'Contact';
      let json;
      try {
        json = await runDataConnector(userId, connector.id, contact.id);
      } catch (err) {
        json = { ok: false, reason: 'error', message: err.message };
      }

      if (!json.ok && (json.reason === 'rate_limited' || json.reason === 'busy' || json.reason === 'ai_failed')) {
        throttles += 1;
        if (throttles > 3) {
          addLog('fa-hand', json.reason === 'ai_failed'
            ? `Could not classify contacts just now — stopped, ${queue.length + 1} left`
            : `${connector.name} keeps asking to slow down — stopped, ${queue.length + 1} left`);
          break;
        }
        const sec = Math.min(300, json.retryAfterSec || 30);
        addLog('fa-hourglass-half', json.reason === 'ai_failed'
          ? `Could not classify ${name} just now — retrying in ${sec}s`
          : `${connector.name} asked for a break — continuing in ${sec}s`);
        queue.unshift(contact);
        await wait(sec * 1000);
        continue;
      }

      if (json.ok) {
        throttles = 0;
        if (onContactUpdate && json.record) {
          onContactUpdate(contact.id, { researchIntelligence: { connectors: { [connector.id]: json.record } }, ...(json.fields || {}) });
        }
        addLog(json.total ? 'fa-plug-circle-check' : 'fa-circle-minus', `${name}: ${json.total ? `${json.total} result${json.total === 1 ? '' : 's'}` : 'nothing found'}`);
        batchResults.push({ id: contact.id, name, status: 'success', total: json.total || 0 });
      } else if (json.reason === 'missing_input' || json.reason === 'contact_missing') {
        addLog('fa-circle-minus', `${name}: ${connectorReasonLabel(json.reason)}`);
        batchResults.push({ id: contact.id, name, status: 'skipped', error: connectorReasonLabel(json.reason) });
      } else {
        addLog('fa-circle-exclamation', `${connector.name}: ${json.reason === 'error' ? json.message : connectorReasonLabel(json.reason)} — stopped. Check it under Settings → Data Sources.`);
        batchResults.push({ id: contact.id, name, status: 'error', error: json.reason });
        setResults([...batchResults]);
        setProgress(batchResults.length);
        break;
      }
      setResults([...batchResults]);
      setProgress(batchResults.length);
    }
    setDone(true);
  }, [userId, contacts, onContactUpdate, addLog]);

  // ── SEO Analyzer batch logic (sequential, mirrors IG pattern) ──
  const startSeoBatch = useCallback(async () => {
    if (!userId || seoContacts.length === 0) return;
    cancelRef.current = false;
    setRunning('seo');
    setTotal(seoContacts.length);
    setProgress(0);
    setResults([]);
    setDone(false);
    startTimeRef.current = Date.now();

    // Load user's SEO settings once (activeModules, country, limits)
    let scanSettings = { activeModules: ['coreMetrics'], country: 'de', limits: { competitors: 3, opportunities: 5 } };
    try {
      const snap = await get(ref(database, `World/${userId}/settings/seoAnalyzerSettings`));
      if (snap.exists()) {
        const s = snap.val() || {};
        scanSettings = {
          activeModules: Array.isArray(s.activeModules) && s.activeModules.length ? s.activeModules : scanSettings.activeModules,
          country: s.country || scanSettings.country,
          limits: {
            competitors: s.limits?.competitors ?? scanSettings.limits.competitors,
            opportunities: s.limits?.opportunities ?? scanSettings.limits.opportunities,
          },
        };
      }
    } catch (_) { /* fall back to defaults */ }

    const batchResults = [];

    for (let i = 0; i < seoContacts.length; i++) {
      if (cancelRef.current) break;

      const contact = seoContacts[i];
      const domain = normalizeDomain(contact.lead_website);
      const result = { id: contact.id, domain, name: contact.business_name || domain };

      try {
        const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.sistrixProxy(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            // Active World id (owner uid for team members) — lets the backend
            // load the OWNER's Sistrix key so every team member can scan.
            userId,
            domain,
            country: scanSettings.country,
            activeModules: scanSettings.activeModules,
            limits: scanSettings.limits,
          }),
        });

        if (!res.ok) {
          const errData = await res.json().catch(() => ({}));
          throw new Error(errData.error || errData.message || `HTTP ${res.status}`);
        }

        const json = await res.json();
        if (!json.success) throw new Error(json.error || 'SEO scan failed');

        const core = json?.data?.core;
        const visibility = core?.visibility?.value;
        const kwTotal = core?.keywordsTotal;
        const kwTop10 = core?.keywordsTop10;

        result.status = 'success';
        result.visibility = visibility;
        result.kwTotal = kwTotal;
        result.sistrixCreditsUsed = json.sistrixCreditsUsed || 0;

        // Build compact [SEO] description block (mirror SeoAnalyzerModal)
        const bits = [];
        if (visibility != null) bits.push(`Sichtbarkeitsindex ${Number(visibility).toFixed(2)}`);
        if (kwTotal != null) bits.push(`${kwTotal} Keywords`);
        if (kwTop10 != null) bits.push(`${kwTop10} in Top 10`);
        const tv = json?.data?.trafficValue;
        if (tv?.estimatedTrafficMonthly != null) bits.push(`~${tv.estimatedTrafficMonthly} Besucher/Monat`);
        if (tv?.estimatedValueMonthly) bits.push(`Traffic-Wert ~${tv.estimatedValueMonthly} €`);
        const vt = json?.data?.visibilityTrend;
        if (vt?.changePct != null) {
          const arrow = vt.direction === 'up' ? '↑' : vt.direction === 'down' ? '↓' : '→';
          bits.push(`Sichtbarkeit ${vt.changePct > 0 ? '+' : ''}${vt.changePct}% (${vt.overWindow || '12M'}) ${arrow}`);
        }
        const topComp = Array.isArray(json?.data?.competitors) && json.data.competitors[0];
        if (topComp?.domain) bits.push(`Top-Wettbewerber: ${topComp.domain}`);
        const bl = json?.data?.backlinks;
        if (bl?.total != null) bits.push(`${bl.total} Backlinks`);
        const topOpp = Array.isArray(json?.data?.opportunities) && json.data.opportunities[0];
        if (topOpp?.keyword) bits.push(`Top-Chance: "${topOpp.keyword}" (Pos ${topOpp.position})`);

        const seoBlock = bits.length ? bits.join(' · ') : '';
        const existingDesc = contact.lead_description || '';
        const descClean = existingDesc.replace(/\n?\[SEO\].*$/s, '').trim();
        const newDesc = seoBlock
          ? (descClean ? `${descClean}\n[SEO] ${seoBlock}` : `[SEO] ${seoBlock}`)
          : descClean;

        const seoAnalysis = {
          domain: json.domain,
          country: json.country,
          scannedAt: json.scannedAt,
          activeModules: json.activeModules || [],
          data: json.data || {},
          sistrixCreditsUsed: json.sistrixCreditsUsed || 0,
        };

        const payload = {
          'researchIntelligence.seoAnalysis': seoAnalysis,
          lead_description: newDesc,
        };
        if (visibility != null) payload.lead_visibility_index = Number(visibility);
        if (kwTotal != null) payload.lead_seo_keywords_total = Number(kwTotal);
        if (kwTop10 != null) payload.lead_seo_keywords_top10 = Number(kwTop10);

        const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
        await updateDoc(contactRef, payload);
        await recordCompanyRecord(userId, contact.id, 'seo', contact, {
          ...contact,
          researchIntelligence: {
            ...(contact.researchIntelligence || {}),
            seoAnalysis,
          },
        });

        try {
          const freshData = { ...contact, ...payload, lead_description: newDesc };
          computeAndStoreSignals(userId, contact.id, freshData, { force: true }).catch(() => {});
        } catch (_) { /* non-fatal */ }

        if (onContactUpdate) {
          // Include the seoAnalysis object so the row's SEO badge/button/tab
          // (which gate on researchIntelligence.seoAnalysis.data) show instantly
          // — without it the data only appeared after a hard refresh. The parent
          // deep-merges researchIntelligence against the live row.
          onContactUpdate(contact.id, {
            researchIntelligence: { seoAnalysis },
            lead_description: newDesc,
            ...(visibility != null ? { lead_visibility_index: Number(visibility) } : {}),
            ...(kwTotal != null ? { lead_seo_keywords_total: Number(kwTotal) } : {}),
            ...(kwTop10 != null ? { lead_seo_keywords_top10: Number(kwTop10) } : {}),
          });
        }
      } catch (err) {
        result.status = 'error';
        result.error = err.message || 'Unknown error';

        // If Sistrix daily rate limit hit, stop the whole batch — further contacts would also 429
        const msg = (err.message || '').toLowerCase();
        if (msg.includes('daily seo scan limit') || msg.includes('429') || msg.includes('rate limit')) {
          batchResults.push(result);
          setResults([...batchResults]);
          setProgress(i + 1);
          break;
        }
      }

      batchResults.push(result);
      setResults([...batchResults]);
      setProgress(i + 1);

      if (i < seoContacts.length - 1 && !cancelRef.current) {
        await new Promise(r => setTimeout(r, SEO_RATE_LIMIT_MS));
      }
    }

    setDone(true);
  }, [userId, seoContacts, onContactUpdate]);

  // ── DM Email Finder batch logic (parallel workers) ──
  const startDmFinderBatch = useCallback(async () => {
    if (!userId || dmFinderContacts.length === 0) return;
    cancelRef.current = false;
    setRunning('dmFinder');
    setTotal(dmFinderContacts.length);
    setProgress(0);
    setResults([]);
    setDone(false);
    setLiveLog([]);

    startTimeRef.current = Date.now();
    const BATCH_SIZE = 3;
    const WORKER_COUNT = 1;
    const batchResults = [];
    let globalFound = 0;
    let globalBlacklisted = 0;
    let creditExhausted = false;
    let progressCount = 0;
    const methodCounts = { standard: 0, expanded: 0, osint: 0, catchAll: 0, notFound: 0, total: 0 };
    // Per-contact tracker for blacklist hits — used in the summary modal
    // to show "X contacts → Blacklist [Remove all]".
    const blacklistedContactsMap = new Map(); // id -> { id, name, emails: [{name, email}] }
    setBlacklistHits([]);

    addLog('fa-crosshairs', `Starting DM Email Finder for ${dmFinderContacts.length} contacts...`);

    // Load the user's global target-market setting once — it localizes Google
    // region, search terms and email patterns per country on the backend.
    let targetCountry = 'auto';
    try {
      const spySnap = await get(ref(database, `/World/${userId}/settings/spySettings`));
      if (spySnap.exists()) targetCountry = spySnap.val()?.advancedSettings?.targetCountry || 'auto';
    } catch (_) { /* fall back to auto-detect */ }

    // Blacklist guard: discovered DM emails belong to contacts that had no
    // email at import time, so they were never checked at import. We
    // re-check per-batch using the scalable `checkBlacklistBatch` callable.
    // Cost = O(found emails + their domains) per batch instead of O(whole
    // blacklist) — at 30k+ entries that's ~20 reads/batch vs 30k reads.
    const batches = [];
    for (let i = 0; i < dmFinderContacts.length; i += BATCH_SIZE) {
      batches.push(dmFinderContacts.slice(i, i + BATCH_SIZE));
    }
    const batchQueue = [...batches];

    async function processBatch() {
      while (batchQueue.length > 0 && !cancelRef.current && !creditExhausted) {
        const batch = batchQueue.shift();
        if (!batch) break;
        const batchCost = batch.length * DM_FINDER_CREDIT_COST;
        const batchNames = batch.map(c => c.business_name || 'Unknown').slice(0, 3).join(', ');

        addLog('fa-coins', `Deducting ${batchCost} credits...`);

        try {
          const deductCreditsFn = httpsCallable(functions, 'deductCredits');
          await deductCreditsFn({
            action: 'dm_email_finder',
            amount: batchCost,
            worldId: userId,
            metadata: { batchSize: batch.length, bulk: true }
          });
        } catch (err) {
          creditExhausted = true;
          addLog('fa-times-circle', 'Not enough credits — stopping');
          for (const c of batch) {
            batchResults.push({ id: c.id, name: c.business_name || 'Unknown', status: 'error', error: 'Not enough credits' });
          }
          setResults([...batchResults]);
          break;
        }

        addLog('fa-search', `Checking patterns for ${batchNames}${batch.length > 3 ? '...' : ''}...`);

        try {
          const payload = batch.map(c => {
            const dms = parseDMs(c.lead_decision_makers);
            return {
              contactId: c.id,
              decisionMakers: dms,
              website: c.lead_website || '',
              existingEmail: c.lead_email || '',
              businessName: c.business_name || '',
              linkedinUrl: c.lead_linkedin || '',
              deepSearch: true,
              country: c.lead_country || '',
              targetCountry,
            };
          });

          const response = await authenticatedFetch(CLOUD_FUNCTION_URLS.dmEmailFinder(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ contacts: payload })
          });

          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const data = await response.json();

          if (data.success && data.results) {
            // Single scalable blacklist lookup for ALL DM emails discovered
            // in this batch. We collect every "found" email up-front and ask
            // the backend in one call — cheaper than `loadBlacklistSets`
            // (which would read the entire blacklist collection) and
            // cheaper than per-email lookups.
            const batchFoundEmails = [];
            for (const cr of data.results) {
              for (const r of (cr.results || [])) {
                if (r.status === 'found' && r.email) batchFoundEmails.push(r.email);
              }
            }
            const blacklistSets = batchFoundEmails.length > 0
              ? await lookupBlacklistForEmails(batchFoundEmails, userId)
              : { emails: new Set(), domains: new Set() };

            for (const contactResult of data.results) {
              const contact = batch.find(c => c.id === contactResult.contactId);
              const rawFoundEmails = (contactResult.results || []).filter(r => r.status === 'found');
              const notFoundDms = (contactResult.results || []).filter(r => r.status === 'not_found' || r.status === 'catch_all');
              const cName = contact?.business_name || 'Unknown';

              // Blacklist guard — split discovered emails into allowed vs.
              // blocked. Blocked ones are treated like "not found" so the
              // DM card doesn't expose a blacklisted address. Blocked
              // contacts are also added to the removal queue so the user
              // can clean them out of the list in one click.
              const blockedEmails = rawFoundEmails.filter(r => isEmailBlacklisted(r.email, blacklistSets));
              const foundEmails = rawFoundEmails.filter(r => !isEmailBlacklisted(r.email, blacklistSets));
              if (blockedEmails.length > 0) {
                globalBlacklisted += blockedEmails.length;
                addLog('fa-ban', `${cName} — ${blockedEmails.length} email${blockedEmails.length > 1 ? 's' : ''} blocked by blacklist`);
                blacklistedContactsMap.set(contactResult.contactId, {
                  id: contactResult.contactId,
                  name: cName,
                  emails: blockedEmails.map(b => ({ name: b.name, email: b.email })),
                });
              }

              if (contact) {
                const allDms = parseDMs(contact.lead_decision_makers);
                let changed = false;

                for (const found of foundEmails) {
                  const dmIdx = allDms.findIndex(dm => dm.name === found.name && (!dm.email || !dm.email.includes('@')));
                  if (dmIdx !== -1) {
                    // Respect the backend's verification flag — catch-all
                    // pattern/generic/O365 hits come back as emailVerified:false
                    // and must NOT be shown as verified.
                    const isVerified = found.emailVerified !== false;
                    const note = found.isGeneric
                      ? 'Allgemeines Postfach (Catch-all) — kein persönliches verifizierbar'
                      : found.discoveryMethod === 'catch_all_pattern'
                        ? 'Catch-all — aus Firmen-Muster abgeleitet (unverifiziert)'
                        : (found.discoveryMethod === 'o365' && !isVerified)
                          ? 'O365 bestätigt — Catch-all-Domain'
                          : null;
                    allDms[dmIdx] = {
                      ...allDms[dmIdx],
                      email: found.email,
                      emailVerified: isVerified,
                      emailSearched: true,
                      emailNotFound: false,
                      emailFinderVersion: 2,
                      source: found.discoveryMethod === 'o365' ? 'o365' : 'dm_finder',
                      ...(found.emailSource ? { emailSource: found.emailSource } : {}),
                      ...(note ? { emailNote: note } : {}),
                    };
                    changed = true;
                  }
                }
                for (const blocked of blockedEmails) {
                  const dmIdx = allDms.findIndex(dm => dm.name === blocked.name && (!dm.email || !dm.email.includes('@')));
                  if (dmIdx !== -1) {
                    allDms[dmIdx] = { ...allDms[dmIdx], emailSearched: true, emailNotFound: true, emailFinderVersion: 2, emailNote: 'Blocked by blacklist' };
                    changed = true;
                  }
                }
                for (const nf of notFoundDms) {
                  const dmIdx = allDms.findIndex(dm => dm.name === nf.name);
                  if (dmIdx !== -1) {
                    allDms[dmIdx] = { ...allDms[dmIdx], emailSearched: true, emailNotFound: true, emailFinderVersion: 2 };
                    changed = true;
                  }
                }

                const payload = {};
                if (changed) payload.lead_decision_makers = JSON.stringify(allDms);

                // Generic company mailbox (info@/kontakt@ …) the finder surfaced
                // is NOT a personal DM address → store it on the contact's
                // general email field instead, merged with any existing value.
                const companyEmail = contactResult.companyEmail;
                if (companyEmail && companyEmail.includes('@') && !isEmailBlacklisted(companyEmail, blacklistSets)) {
                  const existing = String(contact.lead_email || '')
                    .split(/[,;\s]+/)
                    .map(e => e.trim())
                    .filter(Boolean);
                  if (!existing.some(e => e.toLowerCase() === companyEmail.toLowerCase())) {
                    payload.lead_email = [...existing, companyEmail].join(', ');
                  }
                }

                if (Object.keys(payload).length > 0) {
                  try {
                    const contactRef = doc(firestore, `World/${userId}/db/${contact.id}`);
                    await updateDoc(contactRef, payload);
                  } catch (_) {}
                  if (onContactUpdate) onContactUpdate(contact.id, payload);
                }
                logContactActivity(firestore, userId, contact.id, {
                  type: 'dm_emails',
                  title: foundEmails.length > 0
                    ? `DM email finder: ${foundEmails.length} of ${(contactResult.results || []).length} found`
                    : 'DM email finder: no personal email found',
                  detail: [
                    foundEmails.slice(0, 3).map((r) => `${r.name}: ${r.email}${r.emailVerified === false ? ' (unverified)' : ''}`).join(', '),
                    payload.lead_email ? `company inbox ${companyEmail}` : '',
                    blockedEmails.length > 0 ? `${blockedEmails.length} blocked by blacklist` : '',
                    'bulk research',
                  ].filter(Boolean).join(' · '),
                });
              }

              const dmResults = contactResult.results || [];
              for (const dmr of dmResults) {
                methodCounts.total++;
                // Blacklisted hits count as "not found" for the hit-rate
                // stat — the email exists but is unusable, so reporting it
                // as a successful discovery would be misleading.
                const wasBlocked = dmr.status === 'found' && isEmailBlacklisted(dmr.email, blacklistSets);
                if (dmr.status === 'found' && dmr.discoveryMethod && !wasBlocked) {
                  methodCounts[dmr.discoveryMethod] = (methodCounts[dmr.discoveryMethod] || 0) + 1;
                } else if (dmr.status === 'catch_all') {
                  methodCounts.catchAll++;
                } else if (dmr.status === 'not_found' || wasBlocked) {
                  methodCounts.notFound++;
                }
              }
              setDmStats({ ...methodCounts });

              if (foundEmails.length > 0) {
                globalFound += foundEmails.length;
                addLog('fa-check-circle', `${cName} — ${foundEmails.length} email${foundEmails.length > 1 ? 's' : ''} found`);
              } else if (contactResult.isCatchAll) {
                addLog('fa-exclamation-circle', `${cName} — catch-all domain`);
              } else {
                addLog('fa-minus-circle', `${cName} — no match`);
              }

              batchResults.push({
                id: contactResult.contactId,
                name: cName,
                status: foundEmails.length > 0 ? 'success' : contactResult.isCatchAll ? 'catch_all' : 'not_found',
                found: foundEmails.length,
                searched: contactResult.searched || 0,
                isCatchAll: contactResult.isCatchAll,
                emails: foundEmails.map(f => ({ name: f.name, email: f.email, method: f.discoveryMethod || 'standard' })),
              });
            }
          }
        } catch (err) {
          addLog('fa-times-circle', `Batch failed — refunding credits`);
          for (const c of batch) {
            if (!batchResults.some(r => r.id === c.id)) {
              batchResults.push({ id: c.id, name: c.business_name || 'Unknown', status: 'error', error: err.message });
            }
          }
          try {
            const deductCreditsFn = httpsCallable(functions, 'deductCredits');
            await deductCreditsFn({ action: 'dm_email_finder_refund', amount: -batchCost, worldId: userId });
          } catch (_) {}
        }

        progressCount += batch.length;
        setProgress(Math.min(progressCount, dmFinderContacts.length));
        setResults([...batchResults]);
      }
    }

    const workers = Array.from({ length: Math.min(WORKER_COUNT, batches.length) }, () => processBatch());
    await Promise.all(workers);

    setResults([...batchResults]);
    setBlacklistHits([...blacklistedContactsMap.values()]);
    const blockedSuffix = globalBlacklisted > 0
      ? ` (${globalBlacklisted} blocked by blacklist)`
      : '';
    addLog('fa-flag-checkered', `Done — ${globalFound} email${globalFound !== 1 ? 's' : ''} found across ${dmFinderContacts.length} contacts${blockedSuffix}`);
    setDone(true);
    setShowSummary(true);
  }, [userId, dmFinderContacts, addLog]);

  // Kept in a ref so the auto-run effect below reacts only to the real
  // triggers (autoStartDmFinder / contacts) rather than re-firing on every
  // render — startDmFinderBatch is re-created each render since dmFinderContacts
  // is a fresh array.
  const startDmFinderBatchRef = useRef(startDmFinderBatch);
  startDmFinderBatchRef.current = startDmFinderBatch;

  // Auto-run the DM Email Finder as the second leg of the combo. The ref holds
  // the exact contacts batch we already auto-ran for, so a fresh batch (new
  // array reference) re-triggers while the same one never double-fires.
  // startDmFinderBatch no-ops when no contact has a decision maker missing an
  // email, so a research run that surfaced nothing simply ends quietly.
  useEffect(() => {
    if (!autoStartDmFinder) { autoComboRef.current = null; return; }
    if (!contacts || contacts.length === 0) return;
    if (autoComboRef.current === contacts) return;
    autoComboRef.current = contacts;
    cancelRef.current = false;
    // Ultimate Research: if there's nothing for the DM finder to chase (no
    // decision maker missing an email), the batch would no-op silently. Surface
    // the completion modal anyway so the funnel always ends with clear feedback.
    if (autoFinish && dmFinderContacts.length === 0) {
      autoFinishedRef.current = true;
      setUltimateStats({
        started: funnelStats?.started ?? 0,
        removed: funnelStats?.removed ?? 0,
        qualified: funnelStats?.qualified ?? 0,
        emails: 0,
        enriched: 0,
      });
      setUltimateDone(true);
      return;
    }
    startDmFinderBatchRef.current();
  }, [autoStartDmFinder, contacts, autoFinish, dmFinderContacts.length, funnelStats]);

  // Delete all contacts whose discovered DM email matched the blacklist.
  // Returns silently — caller orchestrates the surrounding UI flow
  // ("Confirm All" footer button does sparkle + removal + close in one).
  const removeBlacklistedContacts = useCallback(async () => {
    if (blacklistHits.length === 0) return 0;
    let removed = 0;
    for (const hit of blacklistHits) {
      try {
        await deleteDoc(doc(firestore, `World/${userId}/db/${hit.id}`));
        if (onContactUpdate) onContactUpdate(hit.id, { _deleted: true });
        removed++;
      } catch (err) {
        console.error('[BL-REMOVE] Failed to delete', hit.id, err);
      }
    }
    return removed;
  }, [blacklistHits, userId, onContactUpdate]);

  // Ultimate Research: the DM Email Finder is the final funnel leg and there's
  // nothing left for the user to review, so when `autoFinish` is set we run the
  // exact same side-effects as the summary's "Confirm All" button (remove
  // blacklisted contacts, sparkle the enriched rows) automatically and then show
  // a short completion modal instead of the regular summary.
  useEffect(() => {
    if (!autoFinish || !done || !showSummary || autoFinishedRef.current) return;
    autoFinishedRef.current = true;
    (async () => {
      let removed = 0;
      if (blacklistHits.length > 0) {
        setRemovingBlacklist(true);
        removed = await removeBlacklistedContacts();
        addLog('fa-trash', `${removed} contact${removed === 1 ? '' : 's'} removed (blacklist)`);
        setBlacklistHits([]);
        setRemovingBlacklist(false);
      }
      const successResults = results.filter(r => r.status === 'success');
      const ids = successResults.map(r => r.id).filter(Boolean);
      if (ids.length > 0 && onContactUpdate) {
        ids.forEach(id => onContactUpdate(id, { _sparkle: true }));
      }
      setUltimateStats({
        started: funnelStats?.started ?? 0,
        removed: (funnelStats?.removed ?? 0) + removed,
        qualified: funnelStats?.qualified ?? 0,
        emails: results.reduce((sum, r) => sum + (r.found || 0), 0),
        enriched: successResults.length,
      });
      setShowSummary(false);
      setUltimateDone(true);
    })();
  }, [autoFinish, done, showSummary, blacklistHits, removeBlacklistedContacts, addLog, results, onContactUpdate, funnelStats]);

  // Persist a Potential Analyzer result onto the contact (mirrors
  // usePotentialAnalyzer.applyAnalysis, but writes directly to Firestore since
  // the funnel runs outside that hook). Returns the numeric score.
  const applyFunnelPotential = useCallback(async (contact, analysis) => {
    const score = analysis?.score;
    if (typeof score !== 'number') return null;
    const analysisObj = {
      score,
      fitLabel: analysis.fitLabel || '',
      summary: analysis.summary || '',
      bullets: Array.isArray(analysis.bullets) ? analysis.bullets : [],
      sizeAssessment: analysis.sizeAssessment || 'unknown',
      responseChance: analysis.responseChance || null,
      dataConfidence: analysis.dataConfidence || 'medium',
      analyzedAt: analysis.analyzedAt || new Date().toISOString(),
    };
    const payload = { potential_score: score, potential_analysis: analysisObj };
    const newDescription = buildPotentialNote(contact.lead_description, score, analysis.summary);
    if (newDescription !== null) payload.lead_description = newDescription;
    try {
      await updateDoc(doc(firestore, `World/${userId}/db/${contact.id}`), payload);
      await recordCompanyRecord(userId, contact.id, 'potential', contact, {
        ...contact,
        potential_score: score,
        potential_analysis: analysisObj,
      });
    } catch (_) { /* keep going — local state is still updated below */ }
    if (onContactUpdate) onContactUpdate(contact.id, payload);
    return score;
  }, [userId, onContactUpdate]);

  // ── Smart Funnel: Clean → remove flagged → Potential → keep score > 5 →
  //    (handoff) Web Research → Find Emails ──
  // Stages 1 & 2 run here; stages 3 & 4 reuse the existing Web→DM combo via
  // onStartCombo so that battle-tested path stays untouched.
  const handleStartFunnel = useCallback(async (chosenGuideline, chosenProfile) => {
    if (!onStartCombo) return;
    cancelRef.current = false;
    setRunning('funnel');
    setLiveLog([]);
    setDone(false);
    setResults([]);
    startTimeRef.current = Date.now();

    const deductFn = httpsCallable(functions, 'deductCredits');
    let survivors = [...contacts];

    // Funnel-wide stats accumulated across every stage, handed to the final
    // completion modal so it reflects the WHOLE run (not just the DM finder).
    const funnelStats = { started: contacts.length, removed: 0, qualified: 0 };

    // ===== Stage 1: Clean & remove flagged =====
    setFunnelStage('clean');
    addLog('fa-broom', `Cleaning ${survivors.length} contact${survivors.length === 1 ? '' : 's'}...`);
    try {
      // The cleaner is now explicitly chosen by the user in step 1 (the picker).
      // `undefined` means the picker was bypassed (0/1 guideline) — fall back to
      // the first configured one; `null` means none configured → skip cleaning.
      const guideline = chosenGuideline !== undefined
        ? chosenGuideline
        : ((await loadCleanupGuidelines(userId))[0] || null);
      if (!guideline) {
        addLog('fa-info-circle', 'No cleanup guidelines configured — skipping clean step');
      } else {
        const guidelines = combineGuidelineSections(guideline);
        const isHyper = guideline.hyperIntelligence === true;
        const businessContext = guideline.businessContext || '';
        const shouldCleanNames = guideline.cleanNames !== false;
        const perContact = isHyper ? CLEAN_HYPER_CREDIT_COST : CLEAN_CREDIT_COST;

        // TEMP DIAGNOSTIC — compare against the manual cleaner. Logs the exact
        // cleaner config + per-contact decision so we can see WHY the funnel
        // rejects more (e.g. empty descriptions because clean runs pre-research).
        console.log('[FUNNEL-CLEAN] guideline="%s" hyper=%s keepSection=%s removeSection=%s ctxLen=%d guidelinesLen=%d',
          guideline.name, isHyper, !!guideline.keepSection, !!guideline.removeSection, businessContext.length, guidelines.length);
        console.log('[FUNNEL-CLEAN] guidelines string:\n', guidelines);

        let cleanCharged = true;
        try {
          await deductFn({ action: 'contact_clean', amount: survivors.length * perContact, worldId: userId, metadata: { contactCount: survivors.length, funnel: true, hyperIntelligence: isHyper } });
        } catch (err) {
          const isCreditErr = (err.message || '').includes('Not enough credits') || err?.code === 'resource-exhausted';
          if (isCreditErr) {
            addLog('fa-times-circle', 'Not enough credits for cleaning — funnel stopped');
            setDone(true);
            return;
          }
          cleanCharged = false; // non-credit error: continue without charging (matches AiCleanerPopup)
        }

        const cleanedTimestamp = new Date().toISOString();
        const toRemoveIds = [];
        const kept = [];
        let failed = 0;
        let processed = 0;
        setTotal(survivors.length);
        setProgress(0);

        const queue = [...survivors];
        const CLEAN_CONCURRENCY = 5;
        const cleanWorker = async () => {
          while (queue.length > 0 && !cancelRef.current) {
            let contact = queue.shift();
            try {
              const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.cleanupContact(), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(buildCleanPayload(contact, guidelines, isHyper, businessContext)),
              });
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              const data = await res.json();

              // TEMP DIAGNOSTIC — see exactly what the cleaner decided per contact.
              console.log('[FUNNEL-CLEAN] %s "%s" | descLen=%d websiteLen=%d emailLen=%d → shouldKeep=%s reason="%s"',
                contact.id,
                contact.business_name || '(no name)',
                (contact.lead_description || '').length,
                (contact.lead_website || '').length,
                (contact.lead_email || '').length,
                data.shouldKeep,
                data.reason || '');

              if (data.shouldKeep === false) {
                toRemoveIds.push(contact.id);
              } else {
                const update = { hasCleaned: true, cleanedAt: cleanedTimestamp };
                if (shouldCleanNames && data.cleanedBusinessName && data.cleanedBusinessName !== contact.business_name) {
                  update.business_name = data.cleanedBusinessName;
                  contact = { ...contact, business_name: data.cleanedBusinessName };
                }
                try { await updateDoc(doc(firestore, `World/${userId}/db/${contact.id}`), update); } catch (_) {}
                if (onContactUpdate) onContactUpdate(contact.id, update);
                kept.push(contact);
              }
            } catch (err) {
              // On analysis failure keep the contact (safety) — same as the popup.
              failed++;
              kept.push(contact);
            } finally {
              processed++;
              setProgress(processed);
            }
          }
        };
        await Promise.all(Array.from({ length: Math.min(CLEAN_CONCURRENCY, survivors.length) }, cleanWorker));

        // Refund credits for contacts whose analysis failed (mirrors AiCleanerPopup).
        if (cleanCharged && failed > 0) {
          try { await deductFn({ action: 'contact_clean_refund', amount: -(failed * perContact), worldId: userId, metadata: { failedCount: failed, refund: true, funnel: true } }); } catch (_) {}
        }

        // Safety net: an extreme removal rate on a sizeable batch means the wrong
        // cleaner — abort the whole funnel and delete nothing, so the user can
        // re-run with a proper cleaner instead of losing the list.
        const flaggedRatio = survivors.length > 0 ? toRemoveIds.length / survivors.length : 0;
        if (
          survivors.length >= FUNNEL_REMOVE_SAFETY_MIN &&
          flaggedRatio >= FUNNEL_REMOVE_SAFETY_RATIO
        ) {
          addLog('fa-shield-halved', `Stopped: ${toRemoveIds.length}/${survivors.length} flagged for removal — that's unusually high. Nothing was deleted. Check the cleaner you picked.`);
          setDone(true);
          return;
        }

        // Remove flagged contacts. Delete the docs first (in parallel), THEN push
        // a single bulk UI update — otherwise each per-contact state change made
        // the table re-render once per deletion, which looked like the list
        // "refreshing 5×" before settling.
        const deletedIds = [];
        if (!cancelRef.current && toRemoveIds.length > 0) {
          await Promise.all(toRemoveIds.map(async (id) => {
            try {
              await deleteDoc(doc(firestore, `World/${userId}/db/${id}`));
              deletedIds.push(id);
            } catch (_) {}
          }));
          if (onContactUpdate && deletedIds.length > 0) {
            onContactUpdate(null, { _deletedIds: deletedIds });
          }
        }
        addLog('fa-trash', `Removed ${deletedIds.length} flagged, kept ${kept.length}`);
        funnelStats.removed = deletedIds.length;
        survivors = kept;
      }
    } catch (err) {
      addLog('fa-exclamation-circle', 'Clean step error — continuing with remaining contacts');
    }

    if (cancelRef.current) { setDone(true); return; }
    if (survivors.length === 0) {
      addLog('fa-flag-checkered', 'No contacts left after cleaning');
      setDone(true);
      return;
    }

    // ===== Stage 2: Potential Analyzer & filter (score > 5) =====
    setFunnelStage('potential');
    const eligible = survivors.filter(c => (c.business_name?.trim()) || (c.lead_website?.trim()));
    const profile = chosenProfile || await loadPotentialProfileForRun(userId, null);
    const profileLabel = profile?.name ? ` (${profile.name})` : '';
    addLog('fa-gauge-high', `Analyzing potential${profileLabel} for ${eligible.length} contact${eligible.length === 1 ? '' : 's'}...`);
    let passed = eligible;
    try {
      const scoredPass = [];
      let creditExhausted = false;
      let processed = 0;
      let cursor = 0;
      setTotal(eligible.length);
      setProgress(0);

      const POTENTIAL_CONCURRENCY = 3;
      const potentialWorker = async () => {
        while (cursor < eligible.length && !cancelRef.current && !creditExhausted) {
          const contact = eligible[cursor++];
          try {
            const res = await authenticatedFetch(CLOUD_FUNCTION_URLS.analyzePotential(), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(buildPotentialPayload(contact, profile)),
            });
            // 402 = AI provider out of credits → abort the rest, charge nothing.
            if (res.status === 402) { creditExhausted = true; break; }
            if (res.ok) {
              const result = await res.json();
              if (result.success && result.analysis && typeof result.analysis.score === 'number') {
                // Charge only after a successful analysis (matches usePotentialAnalyzer).
                try {
                  await deductFn({ action: 'potential_analyzer', amount: POTENTIAL_CREDIT_COST, worldId: userId, metadata: { contactId: contact.id, funnel: true } });
                } catch (creditErr) {
                  const isCreditErr = (creditErr.message || '').includes('Not enough credits') || creditErr?.code === 'resource-exhausted';
                  if (isCreditErr) { creditExhausted = true; break; }
                }
                const score = await applyFunnelPotential(contact, result.analysis);
                if (typeof score === 'number' && score > FUNNEL_MIN_SCORE) scoredPass.push(contact);
              }
            }
          } catch (err) {
            /* skip this contact, keep going */
          } finally {
            processed++;
            setProgress(processed);
          }
        }
      };
      await Promise.all(Array.from({ length: Math.min(POTENTIAL_CONCURRENCY, eligible.length) }, potentialWorker));

      if (creditExhausted) {
        addLog('fa-times-circle', 'Potential Analyzer stopped (out of credits) — proceeding with qualified contacts');
      }
      addLog('fa-filter', `${scoredPass.length} of ${eligible.length} scored above ${FUNNEL_MIN_SCORE}`);
      passed = scoredPass;
    } catch (err) {
      addLog('fa-exclamation-circle', 'Potential step error — proceeding with remaining contacts');
    }

    if (cancelRef.current) { setDone(true); return; }
    if (passed.length === 0) {
      addLog('fa-flag-checkered', `No contacts scored above ${FUNNEL_MIN_SCORE}`);
      setDone(true);
      return;
    }

    // ===== Stages 3 & 4: hand off to the existing Web Research → Find Emails combo =====
    funnelStats.qualified = passed.length;
    addLog('fa-arrow-right', `Starting Web Research + Find Emails for ${passed.length} contact${passed.length === 1 ? '' : 's'}`);
    const webContacts = passed.filter(c => (c.business_name?.trim()) || (c.lead_website?.trim()));
    // Clear the funnel toast, then trigger the combo with autoConfirm so it's a
    // true one-click run: the parent auto-confirms the Mass Spy review and the
    // DM Email Finder leg kicks off without any manual click. funnelStats rides
    // along so the final completion modal can show the whole-run numbers.
    setTimeout(() => {
      resetState();
      onStartCombo(webContacts, passed, { autoConfirm: true, funnelStats });
    }, 700);
  }, [contacts, userId, onStartCombo, addLog, onContactUpdate, applyFunnelPotential]);

  // Smart Funnel step 2: pick which ICP profile to score against (if several).
  const openFunnelPotentialPicker = async (guideline) => {
    setProfilePickerLoading(true);
    const arr = await loadPotentialProfiles(userId);
    setProfilePickerLoading(false);
    if (arr.length <= 1) {
      onClose();
      handleStartFunnel(guideline, arr[0] || null);
      return;
    }
    setPendingFunnelGuideline(guideline);
    setPotentialPickerProfiles(arr);
    setPotentialPickerId((arr.find(p => p.isDefault) || arr[0]).id);
    setPotentialPickerMode('funnel');
    setPotentialPicker(true);
  };

  // Smart Funnel step 1: load the configured cleaners and let the user choose.
  // If there's 0 or 1 we skip the picker (nothing to choose) and start directly.
  const openFunnelGuidelinePicker = async () => {
    setFunnelLoadingGuidelines(true);
    const arr = await loadCleanupGuidelines(userId);
    setFunnelLoadingGuidelines(false);
    if (arr.length <= 1) {
      openFunnelPotentialPicker(arr[0] || null);
      return;
    }
    setFunnelGuidelines(arr);
    setFunnelGuidelineId((arr.find(g => g.isDefault) || arr[0]).id);
    setFunnelPicker(true);
  };

  const confirmFunnelGuideline = () => {
    const chosen = funnelGuidelines.find(g => g.id === funnelGuidelineId) || null;
    setFunnelPicker(false);
    openFunnelPotentialPicker(chosen);
  };

  const confirmPotentialProfile = () => {
    const chosen = potentialPickerProfiles.find(p => p.id === potentialPickerId) || null;
    setPotentialPicker(false);
    if (potentialPickerMode === 'funnel') {
      onClose();
      handleStartFunnel(pendingFunnelGuideline, chosen);
      return;
    }
    startSelectedTools(chosen?.id || null);
  };

  const startSelectedTools = (potentialProfileId) => {
    if (selected.web && canSelectWeb) {
      onStartWebSpy(webContacts);
      setWebStarted(true);
    }

    if (selected.websiteAnalyzer && canSelectWa && onStartWebsiteAnalyzer) {
      onStartWebsiteAnalyzer(waContacts.map(c => c.id));
    }

    if (selected.potential && canSelectPotential && onStartPotential) {
      onStartPotential(potentialContacts.map(c => c.id), potentialProfileId || null);
    }

    const runSeo = selected.seo && canSelectSeo;
    const runDm = selected.dmFinder && canSelectDmFinder;
    const runIg = selected.instagram && canSelectIg;
    const runReviews = selected.reviews && canSelectReviews;
    const willRunBg = runDm || runIg || runSeo || runReviews || pickedConnectors.length > 0;

    const chain = [];
    if (runDm) chain.push(startDmFinderBatch);
    if (runIg) chain.push(startIgBatch);
    if (runReviews) chain.push(startReviewBatch);
    if (runSeo) chain.push(startSeoBatch);
    // Custom researchers run in the order they appear in the list.
    for (const connector of pickedConnectors) chain.push(() => startConnectorBatch(connector));

    if (chain.length > 0) {
      (async () => {
        for (const step of chain) {
          if (cancelRef.current) break;
          await step();
        }
      })();
    } else if (!willRunBg) {
      handleClose();
      return;
    }

    if (willRunBg) onClose();
  };

  const openStandalonePotentialPicker = async () => {
    setProfilePickerLoading(true);
    const arr = await loadPotentialProfiles(userId);
    setProfilePickerLoading(false);
    if (arr.length <= 1) {
      startSelectedTools(arr[0]?.id || null);
      return;
    }
    setPotentialPickerProfiles(arr);
    setPotentialPickerId((arr.find(p => p.isDefault) || arr[0]).id);
    setPotentialPickerMode('standalone');
    setPotentialPicker(true);
  };

  const handleStart = () => {
    // Clear any stale cancel flag from a previous run. resetState() (toast
    // auto-dismiss / modal close) sets cancelRef.current = true, which would
    // otherwise make the chain loop below break immediately and silently skip
    // the DM Finder / IG / SEO step on the very next start.
    cancelRef.current = false;

    // Combo mode runs Web Research → DM Email Finder, orchestrated by the
    // parent. It's mutually exclusive with the individual tools, so we hand off
    // and return.
    if (selected.combo && canCombo) {
      handleStartCombo();
      resetState();
      return;
    }

    // Smart Funnel runs Clean → Potential → Web Research → Find Emails. Before
    // starting we ask WHICH cleaner (guideline) to use — picking the wrong one
    // silently rejected almost everything. The picker confirm kicks off the run.
    if (selected.funnel && canFunnel) {
      openFunnelGuidelinePicker();
      return;
    }

    if (selected.potential && canSelectPotential && onStartPotential) {
      openStandalonePotentialPicker();
      return;
    }

    startSelectedTools(null);
  };

  // Combo: run Web Research first, then chain the DM Email Finder on the
  // freshly-enriched contacts. The parent orchestrates the hand-off (it knows
  // when Web Research has applied its data); here we just fire the request.
  const handleStartCombo = () => {
    if (!onStartCombo || !canCombo) return;
    onStartCombo(webContacts, contacts);
  };

  const showToast = running && !isOpen;
  const showModal = isOpen && !running;

  if (!showModal && !showToast && !running && !showSummary && !ultimateDone) return null;

  const lastLog = liveLog.length > 0 ? liveLog[liveLog.length - 1] : null;

  const handleSetNotQualified = async (contactId) => {
    try {
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      await updateDoc(contactRef, { lead_contacted: 'disqualified' });
      const before = (contacts || []).find((c) => c.id === contactId);
      logStatusChange(firestore, userId, contactId, before?.lead_contacted, 'disqualified', 'marked not qualified after research');
      if (onContactUpdate) onContactUpdate(contactId, { lead_contacted: 'disqualified' });
      setResults(prev => prev.map(r => r.id === contactId ? { ...r, _handled: 'disqualified' } : r));
    } catch (_) {}
  };

  const handleRemoveContact = async (contactId) => {
    try {
      const { deleteDoc: delDoc } = await import('firebase/firestore');
      const contactRef = doc(firestore, `World/${userId}/db/${contactId}`);
      await delDoc(contactRef);
      if (onContactUpdate) onContactUpdate(contactId, { _deleted: true });
      setResults(prev => prev.map(r => r.id === contactId ? { ...r, _handled: 'deleted' } : r));
    } catch (_) {}
  };

  const pct = total > 0 ? Math.round((progress / total) * 100) : 0;
  const elapsed = startTimeRef.current ? Math.round((Date.now() - startTimeRef.current) / 1000) : 0;
  const formatTime = (s) => s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
  const totalEmails = results.reduce((sum, r) => sum + (r.found || 0), 0);

  // ── Ultimate Research completion modal ──
  // Shown after the funnel's final DM Email Finder leg auto-confirms. The full
  // per-email summary is intentionally collapsed into one celebratory card —
  // there's nothing left to act on.
  if (ultimateDone) {
    return (
      <div className="brm-overlay" onClick={resetState}>
        <div className="brm-ultimate-done" onClick={e => e.stopPropagation()}>
          <div className="brm-ultimate-done-orbs" aria-hidden="true">
            <span className="brm-ultimate-done-orb brm-ultimate-done-orb--1" />
            <span className="brm-ultimate-done-orb brm-ultimate-done-orb--2" />
            <span className="brm-ultimate-done-orb brm-ultimate-done-orb--3" />
          </div>
          <div className="brm-ultimate-done-icon">
            <i className="fa fa-rocket" />
          </div>
          <h3 className="brm-ultimate-done-title">Ultimate Research Complete</h3>
          <p className="brm-ultimate-done-sub">
            Cleaned, scored, researched &amp; enriched — all in one run.
          </p>
          <div className="brm-ultimate-done-stats">
            {ultimateStats.started > 0 && (
              <div className="brm-ultimate-done-stat">
                <div className="brm-ultimate-done-stat-value">{ultimateStats.started}</div>
                <div className="brm-ultimate-done-stat-label">Analyzed</div>
              </div>
            )}
            {ultimateStats.removed > 0 && (
              <div className="brm-ultimate-done-stat">
                <div className="brm-ultimate-done-stat-value">{ultimateStats.removed}</div>
                <div className="brm-ultimate-done-stat-label">Removed</div>
              </div>
            )}
            <div className="brm-ultimate-done-stat">
              <div className="brm-ultimate-done-stat-value">{ultimateStats.qualified}</div>
              <div className="brm-ultimate-done-stat-label">Qualified</div>
            </div>
            <div className="brm-ultimate-done-stat">
              <div className="brm-ultimate-done-stat-value">{ultimateStats.emails}</div>
              <div className="brm-ultimate-done-stat-label">Email{ultimateStats.emails !== 1 ? 's' : ''} Found</div>
            </div>
          </div>
          <button className="brm-ultimate-done-btn" onClick={resetState}>
            <i className="fa fa-check" /> Done
          </button>
        </div>
      </div>
    );
  }

  // ── Summary Popup ──
  // Skipped entirely for Ultimate Research (autoFinish) — that path auto-confirms
  // via the effect above and shows the completion modal instead.
  if (showSummary && done && !autoFinish) {
    const successResults = results.filter(r => r.status === 'success');
    const errorResults = results.filter(r => r.status === 'error');
    // Count every discovery method that produced a usable email (incl. the
    // catch-all pattern/generic/O365 fallbacks) so the rate reflects actual hits.
    const foundMethods = (dmStats.standard || 0) + (dmStats.expanded || 0) + (dmStats.osint || 0)
      + (dmStats.o365 || 0) + (dmStats.catch_all_pattern || 0) + (dmStats.generic || 0);
    const hitRate = dmStats.total > 0 ? Math.round((foundMethods / dmStats.total) * 100) : 0;

    return (
      <div className="brm-overlay" onClick={() => setShowSummary(false)}>
        <div className="brm-summary-modal" onClick={e => e.stopPropagation()}>

          <div className="brm-summary-hero">
            <div className="brm-summary-hero-icon">
              <i className="fa fa-crosshairs" />
            </div>
            <div className="brm-summary-hero-number">{totalEmails}</div>
            <div className="brm-summary-hero-label">Email{totalEmails !== 1 ? 's' : ''} Found</div>
            <div className="brm-summary-hero-sub">
              {results.length} contacts processed in {formatTime(elapsed)}
            </div>
          </div>

          <div className="brm-summary-body">

            {successResults.length > 0 && (
              <div className="brm-summary-successes">
                <div className="brm-summary-section-title">
                  <i className="fa fa-check-circle" />
                  Verified Emails ({totalEmails})
                </div>
                <div className="brm-summary-success-list">
                  {successResults.map(r => (
                    <div key={r.id} className="brm-summary-success-group">
                      <div className="brm-summary-company-row">
                        <span className="brm-summary-company-name">{r.name}</span>
                      </div>
                      {r.emails && r.emails.length > 0 ? r.emails.map((em, i) => (
                        <div key={i} className="brm-summary-email-row">
                          <i className="fa fa-check-circle" />
                          <span className="brm-summary-email-addr">{em.email}</span>
                          <span className={`brm-summary-stage brm-summary-stage--${em.method === 'standard' ? '1' : em.method === 'expanded' ? '2' : '3'}`}>
                            {em.method === 'standard' ? 'Stage 1' : em.method === 'expanded' ? 'Stage 2' : 'Stage 3'}
                          </span>
                        </div>
                      )) : (
                        <div className="brm-summary-email-row">
                          <i className="fa fa-check-circle" />
                          <span className="brm-summary-email-addr">{r.found} email{r.found !== 1 ? 's' : ''} found</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="brm-summary-grid">
              <div className="brm-summary-stat">
                <div className="brm-summary-stat-value brm-summary-stat--green">{hitRate}%</div>
                <div className="brm-summary-stat-label">Hit Rate</div>
              </div>
              <div className="brm-summary-stat">
                <div className="brm-summary-stat-value">{dmStats.total}</div>
                <div className="brm-summary-stat-label">Searched</div>
              </div>
              <div className="brm-summary-stat">
                <div className="brm-summary-stat-value brm-summary-stat--muted">{dmStats.notFound + dmStats.catchAll}</div>
                <div className="brm-summary-stat-label">No Result</div>
              </div>
            </div>

            {blacklistHits.length > 0 && (
              <div className="brm-summary-blacklist">
                <div className="brm-summary-blacklist-header">
                  <div className="brm-summary-blacklist-title">
                    <i className="fa fa-ban" />
                    <div>
                      <strong>{blacklistHits.length} Contact{blacklistHits.length === 1 ? '' : 's'} → Blacklist</strong>
                      <span className="brm-summary-blacklist-sub">
                        Discovered emails match your blacklist — will be removed when you confirm.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="brm-summary-blacklist-list">
                  {blacklistHits.slice(0, 8).map(hit => (
                    <div key={hit.id} className="brm-summary-blacklist-row">
                      <span className="brm-summary-blacklist-name">{hit.name}</span>
                      <span className="brm-summary-blacklist-email">
                        {hit.emails[0]?.email}{hit.emails.length > 1 ? ` (+${hit.emails.length - 1})` : ''}
                      </span>
                    </div>
                  ))}
                  {blacklistHits.length > 8 && (
                    <div className="brm-summary-blacklist-more">+{blacklistHits.length - 8} more</div>
                  )}
                </div>
              </div>
            )}

            <div className="brm-summary-compliance">
              <i className="fa fa-info-circle" />
              <span>All emails are sourced from publicly available business data. By proceeding, you confirm that you use these results at your own responsibility and in accordance with applicable laws.</span>
            </div>

            {errorResults.length > 0 && (
              <div className="brm-summary-errors">
                <div className="brm-summary-section-title">
                  <i className="fa fa-exclamation-triangle" />
                  {errorResults.length} Error{errorResults.length > 1 ? 's' : ''}
                </div>
                {errorResults.slice(0, 5).map(r => (
                  <div key={r.id} className="brm-summary-error-row">
                    <span className="brm-summary-error-name">{r.name}</span>
                    <span className="brm-summary-error-msg">{r.error || 'Unknown error'}</span>
                  </div>
                ))}
                {errorResults.length > 5 && (
                  <div className="brm-summary-error-more">+{errorResults.length - 5} more</div>
                )}
              </div>
            )}
          </div>

          <div className="brm-summary-footer">
            <button
              className="brm-summary-close-btn"
              disabled={removingBlacklist}
              onClick={async () => {
                // Confirm All — single step that:
                //   1) sparkles the rows whose emails were just applied
                //   2) deletes contacts whose discovered emails matched
                //      the blacklist (no extra confirm dialog — the orange
                //      "X Contacts → Blacklist" block above IS the prompt)
                //   3) closes the summary
                if (blacklistHits.length > 0) {
                  setRemovingBlacklist(true);
                  const removed = await removeBlacklistedContacts();
                  addLog('fa-trash', `${removed} contact${removed === 1 ? '' : 's'} removed (blacklist)`);
                  setBlacklistHits([]);
                  setRemovingBlacklist(false);
                }
                const ids = successResults.map(r => r.id).filter(Boolean);
                if (ids.length > 0 && onContactUpdate) {
                  ids.forEach(id => onContactUpdate(id, { _sparkle: true }));
                }
                setShowSummary(false);
              }}
            >
              {removingBlacklist
                ? <><i className="fa fa-spinner fa-spin" /> Confirming...</>
                : blacklistHits.length > 0
                  ? <><i className="fa fa-check" /> Confirm All &amp; Remove {blacklistHits.length}</>
                  : <><i className="fa fa-check" /> Confirm All</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Toast (background processing) ──
  if (showToast) {
    return (
      <div className={`brm-toast2${toastFading ? ' brm-toast2--fading' : ''}`} onClick={e => e.stopPropagation()}>
        <div className="brm-toast2-progress">
          <div className="brm-toast2-progress-fill" style={{ width: `${pct}%` }} />
        </div>

        <div className="brm-toast2-content">
          <div className="brm-toast2-left">
            <div className={`brm-toast2-icon${done ? ' brm-toast2-icon--done' : running === 'dmFinder' ? ' brm-toast2-icon--dm' : running === 'seo' ? ' brm-toast2-icon--seo' : ''}`}>
              {done
                ? <i className="fa fa-check" />
                : running === 'dmFinder'
                  ? <span className="brm-toast2-spinner" />
                  : running === 'funnel'
                    ? <span className="brm-toast2-spinner" />
                    : running === 'seo'
                      ? <i className="fa fa-magnifying-glass-chart" />
                      : running === 'reviews'
                        ? <i className="fa fa-star-half-stroke" />
                        : running === 'connector'
                          ? <i className="fa fa-plug" />
                          : <i className="fab fa-instagram" />}
            </div>
            <div className="brm-toast2-info">
              <div className="brm-toast2-title">
                {(() => {
                  const label = running === 'dmFinder'
                    ? 'DM Email Finder'
                    : running === 'seo'
                      ? 'SEO Analyzer'
                      : running === 'funnel'
                        ? (funnelStage === 'clean' ? 'Ultimate Research · Cleaning' : funnelStage === 'potential' ? 'Ultimate Research · Potential' : 'Ultimate Research')
                        : running === 'reviews'
                          ? 'Google Review Audit'
                          : running === 'connector'
                            ? runningConnector
                            : 'IG Research';
                  return done ? `${label} Complete` : label;
                })()}
              </div>
              {!done && lastLog && (
                <div className="brm-toast2-log">
                  <i className={`fa ${lastLog.icon}`} />
                  <span>{lastLog.text}</span>
                </div>
              )}
              {done && (
                <div className="brm-toast2-log">
                  <i className="fa fa-flag-checkered" />
                  <span>
                    {bulkResearchDoneLog({
                      running,
                      results,
                      total,
                      totalEmails,
                      elapsedLabel: formatTime(elapsed),
                      lastLogText: lastLog ? lastLog.text : '',
                    })}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="brm-toast2-right">
            {!done && (
              <div className="brm-toast2-stats">
                <span className="brm-toast2-pct">{pct}%</span>
                <span className="brm-toast2-counter">{progress}/{total}</span>
              </div>
            )}
            {running === 'dmFinder' && !done && totalEmails > 0 && (
              <div className="brm-toast2-found">
                <i className="fa fa-envelope" />
                <span>{totalEmails}</span>
              </div>
            )}
            <div className="brm-toast2-actions">
              {done ? (
                <>
                  {running === 'dmFinder' && (
                    <button className="brm-toast2-btn brm-toast2-btn--summary" onClick={() => setShowSummary(true)} title="View Summary">
                      <i className="fa fa-chart-bar" />
                    </button>
                  )}
                  <button className="brm-toast2-btn" onClick={handleDismissToast} title="Dismiss">
                    <i className="fa fa-times" />
                  </button>
                </>
              ) : (
                <button className="brm-toast2-btn brm-toast2-btn--stop" onClick={() => { cancelRef.current = true; }} title="Stop">
                  <i className="fa fa-stop" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Selection Modal ──
  if (!showModal) return null;

  // ── Smart Funnel step 1: choose which cleaner to run ──
  if (funnelPicker) {
    const selectedGl = funnelGuidelines.find(g => g.id === funnelGuidelineId);
    const selectedRules = selectedGl ? countGuidelineRules(selectedGl) : null;
    const noKeepWarning = selectedRules && selectedRules.keep === 0;
    return (
      <div className="brm-overlay" onClick={() => setFunnelPicker(false)}>
        <div className="brm-modal" onClick={e => e.stopPropagation()}>
          <div className="brm-header">
            <div className="brm-header-left">
              <div className="brm-header-icon"><i className="fa fa-broom" /></div>
              <div className="brm-header-info">
                <h3 className="brm-title">Choose Cleaner</h3>
                <span className="brm-subtitle">Pick which cleaning rules Ultimate Research should use</span>
              </div>
            </div>
            <button className="brm-close" onClick={() => setFunnelPicker(false)}><i className="fa fa-times" /></button>
          </div>

          <div className="brm-body brm-body--scroll">
            <div className="brm-funnel-guidelines">
              {funnelGuidelines.map((g) => {
                const rules = countGuidelineRules(g);
                const isSel = g.id === funnelGuidelineId;
                return (
                  <label
                    key={g.id}
                    className={`brm-tool ${isSel ? 'brm-tool--selected' : ''}`}
                    onClick={() => setFunnelGuidelineId(g.id)}
                  >
                    <div className={`brm-checkbox ${isSel ? 'brm-checkbox--checked' : ''}`}>
                      {isSel && <i className="fa fa-check" />}
                    </div>
                    <div className="brm-tool-icon"><i className="fa fa-broom" /></div>
                    <div className="brm-tool-content">
                      <div className="brm-tool-top">
                        <span className="brm-tool-name">{g.name || 'Unnamed cleaner'}</span>
                        {g.isDefault && <span className="brm-tool-count brm-combo-badge">Default</span>}
                      </div>
                      <div className="brm-tool-tags">
                        {rules.legacy > 0 ? (
                          <span className="brm-tag"><i className="fa fa-file-lines" />{rules.legacy} legacy rule{rules.legacy === 1 ? '' : 's'}</span>
                        ) : (
                          <>
                            <span className="brm-tag"><i className="fa fa-circle-check" />{rules.keep} keep</span>
                            <span className="brm-tag"><i className="fa fa-circle-xmark" />{rules.remove} remove</span>
                          </>
                        )}
                        {g.hyperIntelligence && <span className="brm-tag"><i className="fa fa-bolt" />Hyper</span>}
                        {g.emailValidationOnly && <span className="brm-tag"><i className="fa fa-envelope" />Email check only</span>}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>

            {noKeepWarning && (
              <div className="brm-funnel-warning">
                <i className="fa fa-triangle-exclamation" />
                <span>This cleaner has no KEEP rules — it may flag almost every contact for removal. Pick a cleaner with KEEP criteria.</span>
              </div>
            )}
          </div>

          <div className="brm-start-bar brm-start-bar--standalone">
            <span className="brm-footer-hint">
              Then: Potential &gt; {FUNNEL_MIN_SCORE} → Research → Find Emails
            </span>
            <button className="brm-start-btn" disabled={!funnelGuidelineId || profilePickerLoading} onClick={confirmFunnelGuideline}>
              {profilePickerLoading
                ? <><i className="fa fa-spinner fa-spin" /> Loading profiles…</>
                : <><i className="fa fa-arrow-right" /> Continue</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Choose which ICP profile to score against ──
  if (potentialPicker) {
    const isFunnel = potentialPickerMode === 'funnel';
    return (
      <div className="brm-overlay" onClick={() => setPotentialPicker(false)}>
        <div className="brm-modal" onClick={e => e.stopPropagation()}>
          <div className="brm-header">
            <div className="brm-header-left">
              <div className="brm-header-icon"><i className="fa fa-bullseye" /></div>
              <div className="brm-header-info">
                <h3 className="brm-title">Choose ICP profile</h3>
                <span className="brm-subtitle">
                  {isFunnel
                    ? 'Pick which Potential Analyzer profile Ultimate Research should score against'
                    : 'Pick which Potential Analyzer profile to score these contacts against'}
                </span>
              </div>
            </div>
            <button className="brm-close" onClick={() => setPotentialPicker(false)}><i className="fa fa-times" /></button>
          </div>

          <div className="brm-body brm-body--scroll">
            <div className="brm-funnel-guidelines">
              {potentialPickerProfiles.map((p) => {
                const isSel = p.id === potentialPickerId;
                return (
                  <label
                    key={p.id}
                    className={`brm-tool ${isSel ? 'brm-tool--selected' : ''}`}
                    onClick={() => setPotentialPickerId(p.id)}
                  >
                    <div className={`brm-checkbox ${isSel ? 'brm-checkbox--checked' : ''}`}>
                      {isSel && <i className="fa fa-check" />}
                    </div>
                    <div className="brm-tool-icon"><i className="fa fa-bullseye" /></div>
                    <div className="brm-tool-content">
                      <div className="brm-tool-top">
                        <span className="brm-tool-name">{p.name || 'Unnamed profile'}</span>
                        {p.isDefault && <span className="brm-tool-count brm-combo-badge">Default</span>}
                      </div>
                      <div className="brm-tool-tags">
                        {p.strictness && (
                          <span className="brm-tag"><i className="fa fa-sliders" />{p.strictness}</span>
                        )}
                        {p.revenueSize && (
                          <span className="brm-tag"><i className="fa fa-euro-sign" />{p.revenueSize}</span>
                        )}
                        {(p.employeeMin || p.employeeMax) && (
                          <span className="brm-tag">
                            <i className="fa fa-users" />
                            {[p.employeeMin || '…', p.employeeMax || '…'].join('–')}
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="brm-start-bar brm-start-bar--standalone">
            <span className="brm-footer-hint">
              {isFunnel ? `Then: Potential > ${FUNNEL_MIN_SCORE} → Research → Find Emails` : 'Scores 1–10 against this ideal-customer profile'}
            </span>
            <button className="brm-start-btn" disabled={!potentialPickerId} onClick={confirmPotentialProfile}>
              {isFunnel
                ? <><i className="fa fa-rocket" /> Start Ultimate Research</>
                : <><i className="fa fa-play" /> Start Potential</>}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="brm-overlay" onClick={handleClose}>
      <div className="brm-modal" onClick={e => e.stopPropagation()}>

        <div className="brm-header">
          <div className="brm-header-left">
            <div className="brm-header-icon">
              <i className="fa fa-search" />
            </div>
            <div className="brm-header-info">
              <h3 className="brm-title">Start Research</h3>
              <span className="brm-subtitle">{contacts.length} contact{contacts.length === 1 ? '' : 's'} selected · pick what you want to know</span>
            </div>
          </div>
          <button className="brm-close" onClick={handleClose}><i className="fa fa-times" /></button>
        </div>

        <div className="brm-body">
          <div className="brm-tools">

            {funnelPrereqsMet && (
              <label
                className={`brm-tool brm-tool--combo brm-tool--funnel ${selected.funnel ? 'brm-tool--selected' : ''} ${funnelOverLimit ? 'brm-tool--disabled' : ''}`}
                onClick={() => { if (!funnelOverLimit) toggleSelect('funnel'); }}
              >
                <div className="brm-funnel-orbs" aria-hidden="true">
                  <span className="brm-funnel-orb brm-funnel-orb--1" />
                  <span className="brm-funnel-orb brm-funnel-orb--2" />
                  <span className="brm-funnel-orb brm-funnel-orb--3" />
                  <span className="brm-funnel-orb brm-funnel-orb--4" />
                </div>
                <div className={`brm-checkbox ${selected.funnel ? 'brm-checkbox--checked' : ''}`}>
                  {selected.funnel && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon"><i className="fa fa-rocket" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">Ultimate Research</span>
                    <span className="brm-tool-count brm-combo-badge brm-funnel-badge">Ultra</span>
                  </div>
                  <span className="brm-tool-desc">
                    {funnelOverLimit
                      ? `Max ${FUNNEL_MAX_CONTACTS} contacts for this mode — you selected ${contacts.length}. Deselect some to use Ultimate Research.`
                      : 'The full pipeline: cleans the list, removes bad fits, scores the rest and only researches & finds emails for the strongest leads — fully automatic.'}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-broom" />Clean</span>
                    <span className="brm-tag brm-tag--arrow"><i className="fa fa-arrow-right" /></span>
                    <span className="brm-tag"><i className="fa fa-gauge-high" />Potential &gt; {FUNNEL_MIN_SCORE}</span>
                    <span className="brm-tag brm-tag--arrow"><i className="fa fa-arrow-right" /></span>
                    <span className="brm-tag"><i className="fa fa-globe" />Research</span>
                    <span className="brm-tag brm-tag--arrow"><i className="fa fa-arrow-right" /></span>
                    <span className="brm-tag"><i className="fa fa-crosshairs" />Find Emails</span>
                  </div>
                </div>
              </label>
            )}

            {canCombo && (
              <label
                className={`brm-tool brm-tool--combo ${selected.combo ? 'brm-tool--selected' : ''}`}
                onClick={() => toggleSelect('combo')}
              >
                <div className={`brm-checkbox ${selected.combo ? 'brm-checkbox--checked' : ''}`}>
                  {selected.combo && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon"><i className="fa fa-wand-magic-sparkles" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">Research + Find Emails</span>
                    <span className="brm-tool-count brm-combo-badge">Combo</span>
                  </div>
                  <span className="brm-tool-desc">
                    Researches first, then automatically finds the decision-maker emails — one click, no waiting around.
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-globe" />Web Research</span>
                    <span className="brm-tag brm-tag--arrow"><i className="fa fa-arrow-right" />then</span>
                    <span className="brm-tag"><i className="fa fa-crosshairs" />Find Emails</span>
                  </div>
                </div>
              </label>
            )}

            {lockedWeb ? (
              <BrmLockedTool name="Web Research" icon="fa fa-globe" context={researchContext} />
            ) : (
              <label
                className={`brm-tool brm-tool--web ${!canSelectWeb ? 'brm-tool--disabled' : ''} ${selected.web ? 'brm-tool--selected' : ''}`}
                onClick={() => canSelectWeb && toggleSelect('web')}
              >
                <div className={`brm-checkbox ${selected.web ? 'brm-checkbox--checked' : ''}`}>
                  {selected.web && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon"><i className="fa fa-globe" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">Web Research</span>
                    <span className="brm-tool-count">{webContacts.length} of {contacts.length}</span>
                  </div>
                  <span className="brm-tool-desc">
                    {canSelectWeb
                      ? 'A fast read on who they are and what they offer — pulled from across the web.'
                      : 'No contacts with a business name or website yet.'}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-check" />Company overview</span>
                    <span className="brm-tag"><i className="fa fa-check" />Google &amp; web</span>
                    <span className="brm-tag"><i className="fa fa-check" />Social profiles</span>
                  </div>
                </div>
              </label>
            )}

            {lockedWa ? (
              <BrmLockedTool name="Website Analyzer" icon="fa fa-laptop-code" context={researchContext} />
            ) : (
              <label
                className={`brm-tool brm-tool--wa ${!canSelectWa ? 'brm-tool--disabled' : ''} ${selected.websiteAnalyzer ? 'brm-tool--selected' : ''}`}
                onClick={() => canSelectWa && toggleSelect('websiteAnalyzer')}
              >
                <div className={`brm-checkbox ${selected.websiteAnalyzer ? 'brm-checkbox--checked' : ''}`}>
                  {selected.websiteAnalyzer && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon"><i className="fa fa-laptop-code" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">Website Analyzer</span>
                    <span className="brm-tool-count">{waContacts.length} of {contacts.length}</span>
                  </div>
                  <span className="brm-tool-desc">
                    {canSelectWa
                      ? 'Inspects each site: how polished it is, the shop system, and how big the catalog.'
                      : 'No matching contacts — needs a website that hasn\u2019t been analyzed yet.'}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-check" />Shop system</span>
                    <span className="brm-tag"><i className="fa fa-check" />Product count</span>
                    <span className="brm-tag"><i className="fa fa-check" />{analyzerCreditCost || '1–4'} credits / contact</span>
                  </div>
                </div>
              </label>
            )}

            {lockedIg ? (
              <BrmLockedTool name="Instagram Research" icon="fab fa-instagram" iconClass="brm-tool-icon--ig" context={researchContext} />
            ) : (
              <label
                className={`brm-tool brm-tool--ig ${!canSelectIg ? 'brm-tool--disabled' : ''} ${selected.instagram ? 'brm-tool--selected' : ''}`}
                onClick={() => canSelectIg && toggleSelect('instagram')}
              >
                <div className={`brm-checkbox ${selected.instagram ? 'brm-checkbox--checked' : ''}`}>
                  {selected.instagram && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon brm-tool-icon--ig"><i className="fab fa-instagram" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">Instagram Research</span>
                    <span className="brm-tool-count">{igContacts.length} of {contacts.length}</span>
                  </div>
                  <span className="brm-tool-desc">
                    {canSelectIg
                      ? 'Reads the profile: how active they are, how much they engage, and the best way in.'
                      : 'No contacts with an Instagram profile.'}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-check" />How active?</span>
                    <span className="brm-tag"><i className="fa fa-check" />Engagement</span>
                    <span className="brm-tag"><i className="fa fa-check" />AI talking points</span>
                  </div>
                </div>
              </label>
            )}

            {lockedReviews ? (
              <BrmLockedTool name="Google Review Audit" icon="fa fa-star-half-stroke" iconClass="brm-tool-icon--reviews" context={researchContext} />
            ) : (
              <label
                className={`brm-tool brm-tool--reviews ${!canSelectReviews ? 'brm-tool--disabled' : ''} ${selected.reviews ? 'brm-tool--selected' : ''}`}
                onClick={() => canSelectReviews && toggleSelect('reviews')}
              >
                <div className={`brm-checkbox ${selected.reviews ? 'brm-checkbox--checked' : ''}`}>
                  {selected.reviews && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon brm-tool-icon--reviews"><i className="fa fa-star-half-stroke" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">Google Review Audit</span>
                    <span className="brm-tool-count">{reviewContacts.length} of {contacts.length}</span>
                  </div>
                  <span className="brm-tool-desc">
                    {canSelectReviews
                      ? 'Reads their Google reviews: what customers complain about, what they love, and how the owner reacts.'
                      : 'No matching contacts — needs a business name or website without a review audit yet.'}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-check" />Top complaints</span>
                    <span className="brm-tag"><i className="fa fa-check" />Weak spots</span>
                    <span className="brm-tag"><i className="fa fa-check" />{REVIEW_AUDIT_CREDIT_COST} credits / contact</span>
                  </div>
                </div>
              </label>
            )}

            {!lockedConnectors && dataConnectors.map((connector) => {
              const eligible = connectorContactsFor(connector);
              const canPick = eligible.length > 0;
              const picked = connectorPick.includes(connector.id);
              return (
                <label
                  key={connector.id}
                  className={`brm-tool brm-tool--connector ${!canPick ? 'brm-tool--disabled' : ''} ${picked && canPick ? 'brm-tool--selected' : ''}`}
                  onClick={() => canPick && toggleConnector(connector.id)}
                >
                  <div className={`brm-checkbox ${picked && canPick ? 'brm-checkbox--checked' : ''}`}>
                    {picked && canPick && <i className="fa fa-check" />}
                  </div>
                  <div className="brm-tool-icon brm-tool-icon--connector"><i className={`fa ${connector.icon || 'fa-plug'}`} /></div>
                  <div className="brm-tool-content">
                    <div className="brm-tool-top">
                      <span className="brm-tool-name">{connector.name}</span>
                      <span className="brm-tool-count">{eligible.length} of {contacts.length}</span>
                    </div>
                    <span className="brm-tool-desc">
                      {canPick
                        ? (connector.goal || `Pulls data from ${connector.host} for each contact.`)
                        : 'Every selected contact already has data from this source.'}
                    </span>
                    <div className="brm-tool-tags">
                      <span className="brm-tag"><i className="fa fa-plug" />Custom researcher</span>
                      {connector.status === 'error' && (
                        <span className="brm-tag brm-tag--warn"><i className="fa fa-triangle-exclamation" />{connectorReasonLabel(connector.lastError?.reason)}</span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}

            {lockedSeo ? (
              <BrmLockedTool name="SEO Analyzer" icon="fa fa-magnifying-glass-chart" iconClass="brm-tool-icon--seo" context={researchContext} />
            ) : (
              <label
                className={`brm-tool brm-tool--seo ${!canSelectSeo ? 'brm-tool--disabled' : ''} ${selected.seo ? 'brm-tool--selected' : ''}`}
                onClick={() => canSelectSeo && toggleSelect('seo')}
              >
                <div className={`brm-checkbox ${selected.seo ? 'brm-checkbox--checked' : ''}`}>
                  {selected.seo && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon brm-tool-icon--seo"><i className="fa fa-magnifying-glass-chart" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">SEO Analyzer</span>
                    <span className="brm-tool-count">{seoContacts.length} of {contacts.length}</span>
                  </div>
                  <span className="brm-tool-desc">
                    {canSelectSeo
                      ? 'Shows how well they rank on Google — and where the real upside is hiding.'
                      : (!seoKeyReady
                        ? 'No Sistrix API key yet — add it in Research Settings.'
                        : 'No matching contacts — needs a website without an SEO check yet.')}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-check" />Google visibility</span>
                    <span className="brm-tag"><i className="fa fa-check" />Top search terms</span>
                    <span className="brm-tag"><i className="fa fa-check" />Uses your Sistrix key</span>
                  </div>
                </div>
              </label>
            )}

            {lockedPotential ? (
              <BrmLockedTool name="Potential Analyser" icon="fa fa-bullseye" context={researchContext} />
            ) : (
              <label
                className={`brm-tool brm-tool--potential ${!canSelectPotential ? 'brm-tool--disabled' : ''} ${selected.potential ? 'brm-tool--selected' : ''}`}
                onClick={() => canSelectPotential && toggleSelect('potential')}
              >
                <div className={`brm-checkbox ${selected.potential ? 'brm-checkbox--checked' : ''}`}>
                  {selected.potential && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon"><i className="fa fa-bullseye" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">Potential Analyser</span>
                    <span className="brm-tool-count">{potentialContacts.length} of {contacts.length}</span>
                  </div>
                  <span className="brm-tool-desc">
                    {canSelectPotential
                      ? 'Scores how well each contact matches your ideal customer — worth it or not?'
                      : (!potentialEnabled
                        ? 'Switch it on in Research Settings.'
                        : 'No contacts with a business name or website yet.')}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-check" />Fit score 1–10</span>
                    <span className="brm-tag"><i className="fa fa-check" />Right size?</span>
                    <span className="brm-tag"><i className="fa fa-check" />{POTENTIAL_CREDIT_COST} credits / contact</span>
                  </div>
                </div>
              </label>
            )}

            {lockedDmFinder ? (
              <BrmLockedTool name="DM Email Finder" icon="fa fa-crosshairs" iconClass="brm-tool-icon--dm" context={researchContext} />
            ) : (
              <label
                className={`brm-tool brm-tool--dm ${!canSelectDmFinder ? 'brm-tool--disabled' : ''} ${selected.dmFinder ? 'brm-tool--selected' : ''}`}
                onClick={() => canSelectDmFinder && toggleSelect('dmFinder')}
              >
                <div className={`brm-checkbox ${selected.dmFinder ? 'brm-checkbox--checked' : ''}`}>
                  {selected.dmFinder && <i className="fa fa-check" />}
                </div>
                <div className="brm-tool-icon brm-tool-icon--dm"><i className="fa fa-crosshairs" /></div>
                <div className="brm-tool-content">
                  <div className="brm-tool-top">
                    <span className="brm-tool-name">DM Email Finder</span>
                    <span className="brm-tool-count">{dmFinderContacts.length} of {contacts.length}</span>
                  </div>
                  <span className="brm-tool-desc">
                    {canSelectDmFinder
                      ? 'Finds & verifies the decision-maker\u2019s email so you reach the right person directly.'
                      : 'No decision-makers without an email.'}
                  </span>
                  <div className="brm-tool-tags">
                    <span className="brm-tag"><i className="fa fa-check" />Finds the email</span>
                    <span className="brm-tag"><i className="fa fa-check" />Verified &amp; valid</span>
                    <span className="brm-tag"><i className="fa fa-check" />{DM_FINDER_CREDIT_COST} credits / contact</span>
                  </div>
                </div>
              </label>
            )}
          </div>

          {creditBalance !== null && (() => {
            const igCost = selected.instagram ? igContacts.length * IG_RESEARCH_CREDIT_COST : 0;
            const reviewCost = selected.reviews ? reviewContacts.length * REVIEW_AUDIT_CREDIT_COST : 0;
            // Combo's DM leg runs on whatever decision makers research surfaces;
            // we show a rough lower bound from the contacts that already qualify.
            const dmCost = (selected.dmFinder || selected.combo) ? dmFinderContacts.length * DM_FINDER_CREDIT_COST : 0;
            const waCost = selected.websiteAnalyzer ? waContacts.length * (analyzerCreditCost || 1) : 0;
            // SEO scans run on the account's own Sistrix key — no internal credit cost.
            const potentialCost = selected.potential ? potentialContacts.length * POTENTIAL_CREDIT_COST : 0;
            // Funnel lower bound: clean + potential on every selected contact. The
            // Web Research / Find Emails legs depend on how many pass the score
            // gate, so we only show the upfront stages here.
            const funnelCost = selected.funnel ? contacts.length * (CLEAN_CREDIT_COST + POTENTIAL_CREDIT_COST) : 0;
            const totalCost = igCost + reviewCost + dmCost + waCost + potentialCost + funnelCost;
            if (totalCost > 0 && creditBalance < totalCost) {
              return (
                <div className="brm-credit-warning">
                  <i className="fa fa-exclamation-triangle" />
                  <span>Low on credits: {creditBalance} available, ~{totalCost} needed. Some of this may not complete.</span>
                </div>
              );
            }
            if (totalCost > 0) {
              return (
                <div className="brm-credit-info">
                  <i className="fa fa-coins" />
                  <span>Estimated cost: {totalCost} credits ({creditBalance} available)</span>
                </div>
              );
            }
            return null;
          })()}

          <div className="brm-start-bar">
            <span className="brm-footer-hint">
              <i className="fa fa-info-circle" />
              Only contacts with the required data will be processed
            </span>
            <button
              className="brm-start-btn"
              disabled={!hasSelection || funnelLoadingGuidelines || profilePickerLoading}
              onClick={handleStart}
            >
              {funnelLoadingGuidelines ? (
                <><i className="fa fa-spinner fa-spin" /> Loading cleaners…</>
              ) : profilePickerLoading ? (
                <><i className="fa fa-spinner fa-spin" /> Loading profiles…</>
              ) : (
                <><i className="fa fa-play" /> Start Research</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BulkResearchModal;
